import React, { useEffect, useState, useContext, useRef } from 'react';
import { useParams, useHistory } from "react-router-dom";
import { ConfigContext } from "../../App";
import { CLOUDIDS, EVENTTYPES, EVENT_SOURCE, IMS_EVENT, MARKETO_ID, OMEGA_ENV, REGIONS, TEAMS_CONFIG, URLS } from '../../constants';
import { ServiceRegistry } from '../../models/service-registry';
import { SubscriptionService } from '../../models/subscription-service';
import { BsoMapping } from '../../models/mapping-service';
import { PNSWIZARD, RegistryCloud, RegistryProduct, RegistryService } from '../../service-registry.d';
import Spinner from '../spinner/spinner';
import moment from 'moment-timezone';
import { AxiosError, AxiosResponse } from 'axios';
import parse from 'html-react-parser';
import { cloneArrayOfObject, HideFooter, isBizible, removeDuplicate } from '../../utils';
import { CreateSubscriptionErrorType, ServiceRegions, SlackStatus, TeamsStatus, AppInstallAction, SubscriptionType } from '../../proptype';
import ReactTooltip from 'react-tooltip';
import OMEGA from '../../omega';
import GenericError from '../genericerror/genericerror';
import { PublicClientApplication } from '@azure/msal-browser';
declare global {
    interface Window {
        slackWindow: any,
        removeSlackWindow: any,
        teamsWindow: any,
        removeTeamsWindow: any,
        msal: any
    }
}
export default function ProactiveNotification() {
    const eventDefaultValue = {
        element: "Completed subscription",
        type: "Button",
        action: "click",
        widget: {
            name: "proactivenotification",
            type: "component"
        },
        feature: "proactive notification"
    }


    function omega_capture(element: any, widget_name: any, widget_type: any) {
        OMEGA.init().updateAndTrack("event", [{
            ...eventDefaultValue,
            element: element,
            widget: {
                name: widget_name,
                type: widget_type
            },
        }]);
    }

    
    // Initialize MSAL if not already done
    const initializeMSAL = () => {
        if (!window.msal && TEAMS_CONFIG.TEAMS_ENABLED) {
            const msalConfig = {
                auth: {
                    clientId: TEAMS_CONFIG.CLIENT_ID,
                    authority: `https://login.microsoftonline.com/common`,
                    redirectUri: window.location.origin,
                    navigateToLoginRequestUrl: false
                },
                cache: {
                    cacheLocation: 'localStorage',
                    storeAuthStateInCookie: false
                },
                system: {
                    allowNativeBroker: false,
                    allowRedirectInIframe: true
                }
            };
            
            try {
                const msalInstance = new PublicClientApplication(msalConfig);
                window.msal = msalInstance;
                console.log('MSAL initialized in proactivenotification component');
            } catch (error: any) {
                console.error('Failed to initialize MSAL:', error);
                omega_capture(
                    `Error initializing MSAL in proactivenotification: ${error.message || error.toString()}`,
                    "proactivenotification",
                    "error"
                );
            }
        }
    };
    
    // Function to check if Teams should be enabled based on config and user profile
    const checkIsTeamsEnabled = () => {
        if (!JSON.parse(TEAMS_CONFIG.TEAMS_ENABLED)) {
            setIsTeamsEnabled(false);
            return;
        }

        if (!window.adobeIMS?.isSignedInUser()) {
            setIsTeamsEnabled(false);
            return;
        }

        const teamsExternal = JSON.parse(TEAMS_CONFIG.TEAMS_EXTERNAL);
        const teamsExternalRestricted = JSON.parse(TEAMS_CONFIG.TEAMS_EXTERNAL_RESTRICTED_ACCESS);

        // TEAMS_EXTERNAL=true with no restriction means Teams is open to every signed-in user.
        if (teamsExternal && !teamsExternalRestricted) {
            setIsTeamsEnabled(true);
            return;
        }

        window.adobeIMS?.getProfile().then((profile: any) => {
            const userEmail = profile?.email?.toLowerCase() ?? "";
            const isAdobeUser = userEmail.includes("@adobe.com") && profile?.emailVerified === "true";
            const accessList = TEAMS_CONFIG.TEAMS_ACCESS_IDS
                .split(',')
                .map((email: string) => email.trim().toLowerCase());
            const userInAccessList = accessList.includes(userEmail);

            // TEAMS_EXTERNAL=true with restricted access: any signed-in user, but only if allowlisted.
            if (!isAdobeUser && teamsExternal && teamsExternalRestricted) {
                setIsTeamsEnabled(userInAccessList);
                return;
            }

            // TEAMS_EXTERNAL=false: fall back to per-user-type config.
            if (isAdobeUser) {
                if (!JSON.parse(TEAMS_CONFIG.TEAMS_INTERNAL)) {
                    setIsTeamsEnabled(false);
                    return;
                }
                setIsTeamsEnabled(
                    JSON.parse(TEAMS_CONFIG.TEAMS_INTERNAL_RESTRICTED_ACCESS)
                        ? userInAccessList
                        : true
                );
            } else {
                setIsTeamsEnabled(false);
            }
        }).catch((error: any) => {
            console.error('Error checking Teams enablement via profile:', error);
            omega_capture(
                `Error getting profile for Teams enablement check: ${error.message || error.toString()}`,
                "proactivenotification",
                "error"
            );
            setIsTeamsEnabled(false);
        });
    };
    
    const locales = useContext(ConfigContext).appConfig.languageData;
    const [userLanguage, setUserLanguage] = useState("English (United States)");
    const [email, setEmail] = useState("");
    const [cloudList, setCloudList] = useState<any[]>([]);
    const [entitlements, setEntitlements] = useState<string[]>([]);
    const [slackRemoveURL, setSlackRemoveURL] = useState<string>(URLS.REVOKE_SLACK);
    const [loading, setLoading] = useState(false);
    const [pageLoading, setPageLoading] = useState(true);
    const [error, setError] = useState(false);
    const [apiError, setAPIError] = useState(false);
    const [apiErrorType, setAPIErrorType] = useState<CreateSubscriptionErrorType>(CreateSubscriptionErrorType.ClientError);
    const [activeWizard, setActiveWizard] = useState('product');
    const [showCustomRegions, setShowCustomRegions] = useState(false);
    const [showInfo, setShowInfo] = useState({
        slack: false,
        teams: false,
        language: false,
        timeZome: false
    });
    const [bsomapping, setBsoMapping] = useState<any>({});
    const [globalEvent, setGlobalEvent] = useState(true);
    const [globalEventTypes, setGlobalEventTypes] = useState<any[]>([]);
    const [globalRegions, setGlobalRegions] = useState<any[]>([]);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [unsubscribeAll, setUnsubscribeAll] = useState(false);
    const [slackStatus, setSlackStatus] = useState(-1);
    const [teamsStatus, setTeamsStatus] = useState(-1);
    const [slackNotificationEnabled, setSlackNotificationEnabled] = useState(false);
    const [teamsNotificationEnabled, setTeamsNotificationEnabled] = useState(false);
    const [slackSubscriptions, setSlackSubscriptions] = useState<any>({});
    const [teamsSubscriptions, setTeamsSubscriptions] = useState<any>({});
    const [bizibleEnvironmentChecked, setBizibleEnvironmentChecked] = useState(false);
    const [focusedCapability, setFocusedCapability] = useState<string | null>(null);
    const [openCapabilities, setOpenCapabilities] = useState<Set<string>>(new Set());
    const [showSlackConfirm, setShowSlackConfirm] = useState(false);
    const [showTeamsConfirm, setShowTeamsConfirm] = useState(false);
    const [mounted, setMounted] = useState(true);
    const [userTimezone, setUserTimezone] = useState('UTC');
    const [userTimezoneWithOffset, setUserTimezoneWithOffset] = useState('UTC');
    const [slackConfirmInput, setSlackConfirmInput] = useState("");
    const [slackConfirmError, setSlackConfirmError] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState("");
    const [teamsMicrosoftIdInput, setTeamsMicrosoftIdInput] = useState("");
    const [teamsChannelInput, setTeamsChannelInput] = useState("");
    const [teamsMicrosoftIdError, setTeamsMicrosoftIdError] = useState(false);
    const [teamsChannelError, setTeamsChannelError] = useState(false);
    const [initialSlackEnabled, setInitialSlackEnabled] = useState(false);
    const [initialTeamsEnabled, setInitialTeamsEnabled] = useState(false);
    const [isTeamsEnabled, setIsTeamsEnabled] = useState(false);
    const { type }: any = useParams();
    const histroyAPI = useHistory();
    const firstRegionRef = useRef<HTMLInputElement>(null);
    const globalEventRef = useRef<HTMLInputElement>(null);
    const customEventRef = useRef<HTMLInputElement>(null);
    const slackConfirmInputRef = useRef<HTMLInputElement>(null);
    const slackConfirmCancelRef = useRef<HTMLButtonElement>(null);
    const slackConfirmConfirmRef = useRef<HTMLButtonElement>(null);
    const cancelBtnRef = useRef<HTMLButtonElement>(null);
    const backBtnRef = useRef<HTMLButtonElement>(null);
    const continueBtnRef = useRef<HTMLButtonElement>(null);
    const emailCheckboxRef = useRef<HTMLInputElement>(null);
    const slackCheckboxRef = useRef<HTMLInputElement>(null);
    const noneRadioRef = useRef<HTMLInputElement>(null);
    const confirmPrimaryBtnRef = useRef<HTMLButtonElement>(null);

    // Fix variable names for services
    const serviceRegistry: ServiceRegistry = ServiceRegistry.init();
    const bsoMapping: BsoMapping = BsoMapping.init();
    const subscriptionService: SubscriptionService = SubscriptionService.init();
    subscriptionService.setLocales(locales);

    useEffect(() => {
        document.title = "Create Subscription - Adobe System status";
        
        // Initialize MSAL for Teams functionality
        initializeMSAL();
        
        // Handler for when IMS becomes ready
        const handleIMSReady = () => {
            checkIsTeamsEnabled();
            getAllData();
        };
        
        setGlobalEventTypes(cloneArrayOfObject(EVENTTYPES));
        setGlobalRegions(cloneArrayOfObject(REGIONS));
        if (window.adobeIMS) {
            // Check Teams enabled status after IMS is ready
            handleIMSReady();
        } else {
            document.addEventListener(IMS_EVENT, handleIMSReady);
        }

        const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') ReactTooltip.hide(); };
        document.addEventListener('keydown', handler);
        
        return () => {
            setMounted(false);
            document.removeEventListener(IMS_EVENT, handleIMSReady);
            document.removeEventListener('keydown', handler);
        }
    }, []);

    useEffect(() => {
        if (activeWizard === PNSWIZARD.Region) {
            if (globalEventRef.current) {
                globalEventRef.current.focus();
            } else if (firstRegionRef.current) {
                firstRegionRef.current.focus();
            } else if (continueBtnRef.current) {
                continueBtnRef.current.focus();
            } else if (backBtnRef.current) {
                backBtnRef.current.focus();
            } else if (cancelBtnRef.current) {
                cancelBtnRef.current.focus();
            }
        }
    }, [activeWizard]);

    useEffect(() => {
        if (activeWizard === PNSWIZARD.Notification) {
            // Focus on the currently selected notification channel radio button
            setTimeout(() => {
                if (slackNotificationEnabled && slackCheckboxRef.current) {
                    slackCheckboxRef.current.focus();
                } else if (teamsNotificationEnabled) {
                    const teamsRadio = document.querySelector('input[value="teams"]') as HTMLInputElement;
                    if (teamsRadio) {
                        teamsRadio.focus();
                    }
                } else if (noneRadioRef.current) {
                    noneRadioRef.current.focus();
                }
            }, 100);
        }
    }, [activeWizard, slackNotificationEnabled, teamsNotificationEnabled]);

    useEffect(() => {
        if (showSlackConfirm) {
    
            setTimeout(() => {
                if (slackConfirmInputRef.current) {
                    slackConfirmInputRef.current.focus();
                }
            }, 100);
        }
    }, [showSlackConfirm]);

    useEffect(() => {
        if (!showSlackConfirm) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                setShowSlackConfirm(false);
            }

            if (e.key === 'Tab') {
                e.preventDefault();

                const focusableElements = document.querySelectorAll(
                    '.slack-confirm input, .slack-confirm button'
                );
                const firstElement = focusableElements[0] as HTMLElement;
                const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

                if (e.shiftKey) {
                    if (document.activeElement === firstElement) {
                        lastElement?.focus();
                    } else {
                        const currentIndex = Array.from(focusableElements).indexOf(document.activeElement as HTMLElement);
                        if (currentIndex > 0) {
                            (focusableElements[currentIndex - 1] as HTMLElement).focus();
                        }
                    }
                } else {
                    if (document.activeElement === lastElement) {
                        firstElement?.focus();
                    } else {
                        const currentIndex = Array.from(focusableElements).indexOf(document.activeElement as HTMLElement);
                        if (currentIndex < focusableElements.length - 1) {
                            (focusableElements[currentIndex + 1] as HTMLElement).focus();
                        }
                    }
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [showSlackConfirm]);

    useEffect(() => {
        if (!showTeamsConfirm) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                setShowTeamsConfirm(false);
                setTeamsNotificationEnabled(false);
            }

            if (e.key === 'Tab') {
                e.preventDefault();

                const focusableElements = document.querySelectorAll(
                    '.teams-confirm input, .teams-confirm button'
                );
                const firstElement = focusableElements[0] as HTMLElement;
                const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

                if (e.shiftKey) {
                    if (document.activeElement === firstElement) {
                        lastElement?.focus();
                    } else {
                        const currentIndex = Array.from(focusableElements).indexOf(document.activeElement as HTMLElement);
                        if (currentIndex > 0) {
                            (focusableElements[currentIndex - 1] as HTMLElement).focus();
                        }
                    }
                } else {
                    if (document.activeElement === lastElement) {
                        firstElement?.focus();
                    } else {
                        const currentIndex = Array.from(focusableElements).indexOf(document.activeElement as HTMLElement);
                        if (currentIndex < focusableElements.length - 1) {
                            (focusableElements[currentIndex + 1] as HTMLElement).focus();
                        }
                    }
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [showTeamsConfirm]);

    useEffect(() => {
        if (activeWizard === PNSWIZARD.Confirm) {
            // Focus on the primary confirm button when confirmation page loads
            setTimeout(() => {
                if (confirmPrimaryBtnRef.current) {
                    confirmPrimaryBtnRef.current.focus();
                }
            }, 100);
        }
    }, [activeWizard]);

    function getAllData() {
        if (!window.adobeIMS?.isSignedInUser()) {
            window.adobeid.redirect_uri = window.location.href;
            window.adobeIMS.signIn();
            return;
        }

        window.scrollTo(0, 0);

        setUserLanguage(subscriptionService.getUserDetail().userLanguage);
        setEmail(subscriptionService.getUserDetail().email);

        let pageType = isEdit() ? "edit" : "create";
        let hierarchy_web_slack = "";
        if (window.location.hash){

                hierarchy_web_slack = `home:manage-subscription:${pageType}:${window.location.hash}`
            }
        else{
                hierarchy_web_slack = `home:manage-subscription:${pageType}`
            }
        OMEGA.init().updateAndTrack('page', {
            hierarchy: hierarchy_web_slack,
            name: `Manage Subscription ${pageType} - Adobe status`,
            url: encodeURIComponent(window.location.href),
            type: "pns",
            env: OMEGA_ENV
        });

        if (window.location.hash === '#adminconsole') {
            OMEGA.init().updateAndTrack("event", [{
                element: "Manage Subscription",
                action: "page-load",
                type: "referral",
                widget: {
                    name: "manage-subscription",
                    type: "pns"
                },
                feature: "adminconsole"
            }]);
        }

        subscriptionService.getSlackSubscription().then((result: any) => {
            let slackSubscriptionData = result.data;
            if (slackSubscriptionData?.channel && slackSubscriptionData?.active) {
                setSlackSubscriptions(slackSubscriptionData);
                setSlackRemoveURL(slackSubscriptionData.workSpaceUrl);
                setInitialSlackEnabled(true);
            }
        });

        subscriptionService.getTeamsConversations().then((result: any) => {
            let teamsSubscriptionData = result.data;
            if (teamsSubscriptionData[0]?.isActive) {
                setTeamsSubscriptions(teamsSubscriptionData);
                setInitialTeamsEnabled(true);
            }
        });

        Promise.all([subscriptionService.getSubscriptions(), serviceRegistry.fetch(true), bsoMapping.fetch(true)])
            .then((result: any) => [result[0].data, result[1].data, result[2].data])    
            .then((result: any) => {
                if (mounted) {
                    setBsoMapping(result[2]);
                    let [subscriptionResult]: any = result;
                    setSlackStatus(subscriptionResult.slackStatus);
                    setTeamsStatus(subscriptionResult.teamsStatus);
                    setSlackNotificationEnabled(subscriptionResult.slackNotificationEnabled);
                    setTeamsNotificationEnabled(subscriptionResult.teamsNotificationEnabled);

                    if (isEdit()) {
                        setCloudList(serviceRegistry.getUpdatedCloudList(serviceRegistry.mergeOfferingsIntoProduct(subscriptionResult), (_isBizibleEvironmentSelected: boolean) => {
                            setBizibleEnvironmentChecked(_isBizibleEvironmentSelected);
                        },result[2]));
                    }
                    setPageLoading(false);
                }
            })
            .catch((error: any) => {
                console.error('Error in proactivenotification service promises:', error);
                omega_capture(
                    `Error in proactivenotification service promises: ${error.message || error.toString()}`,
                    "proactivenotification",
                    "error"
                );
                setPageLoading(false);
                setError(true);
            });

        if (isEdit()) {
            setGlobalEvent(false);
        } else {
            Promise.all([subscriptionService.getEntitlementCodeFromAPS(), serviceRegistry.fetch(true)])
                .then((result) => {
                    if (mounted) {
                        let _entitlements: string[] = result[0],
                            _allServiceCodes = serviceRegistry.getServiceCodeMap();

                        _entitlements = _entitlements.filter((code: string) => {
                            return _allServiceCodes.has(code);
                        });

                        if (_entitlements.length) {
                            setEntitlements(_entitlements);
                            setCloudList(serviceRegistry.getProductsByCloud(_entitlements));
                        } else {
                            setCloudList(serviceRegistry.getProductsByCloud());
                        }
                        setPageLoading(false);
                    }
                })
                .catch((error: any) => {
                    console.error('Error in proactivenotification entitlement promises:', error);
                    omega_capture(
                        `Error in proactivenotification entitlement promises: ${error.message || error.toString()}`,
                        "proactivenotification",
                        "error"
                    );
                    setPageLoading(false);
                    setError(true);
                });
        }

    }
    function isEdit() {
        return type === 'edit';
    }

    function isCloudOpened() {
        return cloudList.some((cloud: RegistryCloud) => cloud.isOpen);
    }

    function isNoProductSelected() {
        return !cloudList.filter((cloud: RegistryCloud) => {
            return cloud.cloudProducts.some((product: RegistryProduct) => product.active)
        }).length;
    }

    function disableButton() {
        return activeWizard === PNSWIZARD.Product && !isEdit() && isNoProductSelected()
    }

    function getSelectedProducts() {

        return cloudList.reduce((products: RegistryProduct[], cloud: RegistryCloud) => {
            Array.prototype.push.apply(products, cloud.cloudProducts.filter((product: RegistryProduct) => product.active));
            return products;
        }, []);
    }

    function hasEnvironments() {
        return cloudList.some((cloud: RegistryCloud) => cloud.cloudProducts.some((product: RegistryProduct) => product.active && product.showEnvironment))
    }

    function groupByDataCenter(product: RegistryProduct) {
       
        let result: any = [];
        if (product.productOfferings?.length) {
            result = product.productOfferings
                .filter((offering: RegistryService) => offering.active)
                .reduce((dataCenters: any[], offering: RegistryService) => {
                    Array.prototype.push.apply(dataCenters, offering.serviceDataCenters);
                    return dataCenters;
                }, []);
        }

        return removeDuplicate(result, 'name')
            .sort((a: any, b: any) => a.name.localeCompare(b.name));
    }

    function validateTimeZone(callback: Function) {
        setAPIError(false);
        setLoading(true);
        setLoadingMessage("Loading...");
        subscriptionService.validateTimeZone(moment.tz.guess())
            .then((response: AxiosResponse) => response.data)
            .then((response: any) => {
                if (response.timeZone === "UTC") {
                    setUserTimezoneWithOffset(response.timeZone);
                } else {
                    setUserTimezoneWithOffset(
                        response.timeZone +
                        " (" +
                        response.timezoneOffset +
                        ")"
                    );
                }
                setUserTimezone(response.timeZone);
                window.scrollTo(0, 0);
                setLoading(false);
                setLoadingMessage("");
                callback();
            })
            .catch((error: any) => {
                console.error('Error in proactivenotification timezone validation:', error);
                omega_capture(
                    `Error in proactivenotification timezone validation: ${error.message || error.toString()}`,
                    "proactivenotification",
                    "error"
                );
                let offset = new Date().getTimezoneOffset(),
                    absOffset = Math.abs(offset);

                let currentTimezone = moment.tz.guess();
                setUserTimezone(currentTimezone);
                setUserTimezoneWithOffset(
                    currentTimezone +
                    " (" +
                    "UTC " +
                    (offset > 0 ? "-" : "+") +
                    Math.floor(absOffset / 60) +
                    ":" +
                    (absOffset % 60) +
                    ")"
                );
                window.scrollTo(0, 0);
                setLoading(false);
                setLoadingMessage("");
                callback();
            });
    }

    function gotoConfrim() {
        if (globalEvent) {
            updatedProducts();
        }
        setActiveWizard(PNSWIZARD.Confirm);
    }

    function updatedProducts() {

        selectedProducts.forEach((product: RegistryProduct) => {
            if (product.productOfferings?.length) {
                product.productOfferings = product.productOfferings.map((offering: RegistryService) => {
                    if (offering.active) {
                        offering.regions = cloneArrayOfObject(globalRegions);
                    }
                    return offering;
                });
            }

            product.regions = cloneArrayOfObject(globalRegions);
            product.eventTypes = cloneArrayOfObject(globalEventTypes);
            return product;
        });
        setSelectedProducts([...selectedProducts]);
    }

    function hideAllDataCenters(dataCenters: any[]) {
        return dataCenters.map((dataCenter: any) => {
            dataCenter.isOpen = false
            return dataCenter;
        });
    }

    function parseEnvironmentName(name: string) {
        let split = name.split(/-(.+)/);
        if (split.length > 1) return split[1];

        return name;
    }

    function getCloudName(productId: string) {
        return serviceRegistry.getCloudName(productId);
    }

    function stringifyOffer(offerings?: RegistryService[]) {
        if (offerings?.length) {
            return offerings?.filter((offering: RegistryService) => offering.active)
                .map((offering: RegistryService) => offering.name)
                .join(', ');
        }
        return ''
    }

    function joinEvents(eventTypes?: any[]) {
        if (eventTypes?.length) {
            return eventTypes.filter((eventType: any) => eventType.active)
                .map((eventType: any) => locales[eventType.name as keyof typeof locales])
                .join(', ');
        }
        return '';
    }

    function stringifyRegion(regions?: any[]) {
        if (regions?.length) {
            return regions.filter((region: any) => region.active)
                .map((region: any) => region.name)
                .join(', ');
        }
        return '';
    }

    function stringifyEnvironment(product: RegistryProduct) {

        let selectedBizibleEnvironments: string[] = [], enviroments: string[] = [];
        if (product.productOfferings?.length) {
            let selectedOfferings: RegistryService[] = [];
            product.productOfferings.forEach((offering: RegistryService) => {
                if (offering.active) {
                    selectedOfferings.push(offering);

                    if (isBizible(offering)) {
                        if (offering?.serviceEnvironments?.length) {
                            Array.prototype.push.apply(selectedBizibleEnvironments, offering.serviceEnvironments.filter((environment: RegistryService) => environment.active)
                                .map((environment: RegistryService) => environment.id));
                        }
                    }
                }


            });

            enviroments = selectedOfferings.reduce((enviroments: string[], offering: RegistryService) => {
                if (offering?.serviceEnvironments?.length) {
                    Array.prototype.push.apply(enviroments, offering.serviceEnvironments.filter((environment: RegistryService) => environment.active && !selectedBizibleEnvironments.includes(environment.id))
                        .map((environment: RegistryService) => environment.name));
                }
                return enviroments;
            }, []);

            enviroments = enviroments.filter((enviroment: string, position: number) => enviroments.indexOf(enviroment) === position);

            if (selectedBizibleEnvironments.length) {
                enviroments.push(ServiceRegions.Americas);
            }

        } else {
            if (product?.serviceEnvironments?.length) {
                enviroments = product.serviceEnvironments?.filter((environment: RegistryService) => environment.active && !selectedBizibleEnvironments.includes(environment.id))
                    .map((environment: RegistryService) => environment.name);
            }
        }
        enviroments = enviroments.filter(function(item:any, pos:any) {
            return enviroments.indexOf(item) === pos;
        })
        return enviroments.sort((a: string, b: string) => a.localeCompare(b))
            .join(', ');
    }

    function redirect() {
        histroyAPI.push({
            pathname: "/proactive-notifications/manage",
            state: {
                page: 'pns',
                slackChannelName: slackConfirmInput
            }
        });
    }

    function subscribe(actions: {
        slackAction?: AppInstallAction;
        teamsAction?: AppInstallAction;
    } = {}) {
        const slackAction = actions.slackAction ?? AppInstallAction.None;
        const teamsAction = actions.teamsAction ?? AppInstallAction.None;
        
        let type = isEdit() ? "edit subscription" : "create subscription";
        setLoading(true);
        setLoadingMessage("Processing subscription...");
        if (unsubscribeAll) {
            subscriptionService.deleteSelfSubscription()
                .then(() => {
                    subscriptionService.clearMyEventsContextCache();
                    setLoading(false);
                    setLoadingMessage("");
                    histroyAPI.push({
                        pathname: "/proactive-notifications/manage",
                        state: {
                            page: 'pns'
                        }
                    });
                })
                .catch((error: any) => {
                    console.error('Error in proactivenotification unsubscribe all:', error);
                    omega_capture(
                        `Error in proactivenotification unsubscribe all: ${error.message || error.toString()}`,
                        "proactivenotification",
                        "error"
                    );
                    setLoading(false);
                    setLoadingMessage("");
                    setAPIError(true);
                })
        } else {
            let nscommaction = false;
            let payload = {
                timeZone: userTimezone,
                language: userLanguage || "English (United States)",
                source: EVENT_SOURCE,
                subscriptions: generatePayloadData(),
                slackNotificationEnabled: slackNotificationEnabled,
                teamsNotificationEnabled: teamsNotificationEnabled
            }

            const hasSetupAction = slackAction !== AppInstallAction.None || teamsAction !== AppInstallAction.None;
            if(hasSetupAction){
                if(teamsAction === AppInstallAction.None && (slackAction === AppInstallAction.Setup || slackAction === AppInstallAction.Modify)){
                    nscommaction = true;
                    installSlack();
                } 
                else if(slackAction === AppInstallAction.Setup && teamsAction === AppInstallAction.Revoke){
                    //If user is switching from Teams to Slack
                    //Disable teams without redirecting to channel removal steps and setup slack
                    //This is to skip using /setup-channels page
                    nscommaction = true;    
                    installSlack();
                }else if(teamsAction === AppInstallAction.Setup || teamsAction === AppInstallAction.Modify){
                    //Teams should be handled after subscription completion (nscommaction remains false)
                    installTeams(false);
                }
            }

            

            subscriptionService.subscibeProducts(payload)
                .then(() => {
                    subscriptionService.clearMyEventsContextCache();
                    OMEGA.init().updateAndTrack("event", [{
                        ...eventDefaultValue,
                        element: "Completed subscription",
                        attributes: {
                            completedType: type
                        }
                    }]);
                    setLoading(false);
                    setLoadingMessage("");
                    
                    if (hasSetupAction && nscommaction===false) {
                        if(teamsAction === AppInstallAction.None && slackAction === AppInstallAction.Revoke){
                            window.removeSlackWindow = window.open(slackRemoveURL, "_blank");
                            redirect();
                            OMEGA.init().updateAndTrack("event", [{
                                ...eventDefaultValue,
                                element: "Remove slack",
                                widget: {
                                    name: "Slack",
                                    type: "panel"
                                },
                                attributes: {
                                    completedType: type
                                }
                            }]);
                        } else if (slackAction === AppInstallAction.None && (teamsAction === AppInstallAction.Setup || teamsAction === AppInstallAction.Modify)) {
                            // Notify Teams window that subscription is complete
                            notifyTeamsSubscriptionComplete();
                            redirect();
                        } else if (slackAction === AppInstallAction.None && teamsAction === AppInstallAction.Revoke) {
                            window.removeTeamsWindow = window.open('/teams/landing#remove-step2', "_blank");
                            redirect();
                        } else if(slackAction === AppInstallAction.Revoke && teamsAction === AppInstallAction.Setup) {
                            //If user is switching from Teams to Slack
                            //Disable slack without redirecting to revoke page and install teams
                            //This is to skip using /setup-channels page
                            // Notify Teams window that subscription is complete
                            notifyTeamsSubscriptionComplete();
                            redirect();
                        }else{
                            // Open setup-channels with teamsAction and slackAction details
                            histroyAPI.push({
                                pathname: "/setup-channels",
                                state: {
                                    slackAction: slackAction,
                                    teamsAction: teamsAction,
                                    slackStatus: slackStatus,
                                    teamsStatus: teamsStatus
                                }
                            });
                        }
                    }else{
                        redirect();
                    }
                })
                .catch((error: AxiosError) => {
                    console.error('Error in proactivenotification subscription creation:', error);
                    omega_capture(
                        `Error in proactivenotification subscription creation: ${error.message || error.toString()}`,
                        "proactivenotification",
                        "error"
                    );
                    OMEGA.init().updateAndTrack("event", [{
                        ...eventDefaultValue,
                        element: "Failed subscription",
                        attributes: {
                            completedType: type
                        }
                    }]);
                    setLoading(false);
                    setLoadingMessage("");
                    if (error.response?.status && error.response?.status >= 500) {
                        setAPIErrorType(CreateSubscriptionErrorType.ServerError);
                    } else {
                        setAPIErrorType(CreateSubscriptionErrorType.ClientError);
                    }
                    setAPIError(true);

                })
        }

    }

    function installSlack() {
        if (!window.adobeIMS?.isSignedInUser()) {
            window.adobeid.redirect_uri = window.location.href;
            window.adobeIMS.signIn();
            return;
        }
        let URL = URLS.ADD_TO_SLACK_URL + "&code=" + window.adobeIMS?.getAccessToken().token;
        window.slackWindow = window.open(URL, "_blank");
        // redirect();
        OMEGA.init().updateAndTrack("event", [{
            ...eventDefaultValue,
            element: "Add slack",
            widget: {
                name: "Slack",
                type: "panel"
            },
            attributes: {
                completedType: type
            }
        }]);
    }

    function installTeams(shouldRedirect: boolean = true) {
        if (!window.adobeIMS?.isSignedInUser()) {
            window.adobeid.redirect_uri = window.location.href;
            window.adobeIMS.signIn();
            return;
        }

        // Store Microsoft ID in sessionStorage
        sessionStorage.setItem('teams_setup_microsoft_id', teamsMicrosoftIdInput);

        const teamsSetupUrl = `/teams-setup`;
        window.teamsWindow = window.open(teamsSetupUrl, "_blank");
        
        if (shouldRedirect) {
            redirect();
        }
        
        OMEGA.init().updateAndTrack("event", [{
            ...eventDefaultValue,
            element: "Add teams",
            widget: {
                name: "Teams",
                type: "panel"
            },
            attributes: {
                completedType: type
            }
        }]);
    }

    function notifyTeamsSubscriptionComplete() {
        console.log('Notifying Teams window that subscription is complete');
        if (window.teamsWindow) {
            window.teamsWindow.postMessage({
                type: 'SUBSCRIPTION_COMPLETE',
                timestamp: Date.now()
            }, window.location.origin);
        }
    }

    function showAPIError() {

        if (apiError && apiErrorType === CreateSubscriptionErrorType.ServerError) {
            return <div className="error-msg show">{parse(locales.subscription_progress_error)}</div>
        } else if (apiError && apiErrorType === CreateSubscriptionErrorType.ClientError) {
            return <div className="error-msg show">{parse(locales.subscription_error_info)}</div>
        } else {
            return <></>
        }

    }

    function isAllOfferSelected(product: RegistryProduct) {
        if (product.productOfferings?.length) {
            return product.productOfferings?.length === product.productOfferings?.filter((offering: RegistryService) => offering.active).length
        }

        return false;
    }

    function isMarketo(product: RegistryProduct) {
        return (product.id === MARKETO_ID || product.name.toLowerCase().indexOf('marketo')) !== -1;
    }

    function generatePayloadData() {
        return selectedProducts.map((product: RegistryProduct) => {
            let regions: any = [],
                result: any = {
                    product: {
                        id: product.id,
                        name: product.name,
                        type: product.productOfferings?.length && !isAllOfferSelected(product) ? SubscriptionType.Offering : SubscriptionType.Product
                    },
                    cloudName: getCloudName(product.id),
                    eventType: product.eventTypes?.filter((eventType: any) => eventType.active).map((eventType: any) => eventType.id),
                    regions: [] // will be updated if no offering
                };

            if (isMarketo(product)) { // special case, for marketo always type should be 2
                result.product.type = SubscriptionType.Offering;
            }

            if (!product.showEnvironment) {
                regions = product.regions?.filter((region: any) => region.active)
                    .map((region: any) => { return { id: region.id, name: region.name } })
            }
            if (product.productOfferings?.length) {
                result.productOfferings = product.productOfferings
                    .filter((offering: RegistryService) => offering.active)
                    .reduce((productOfferings: any[], offering: RegistryService) => {
                        let outOffering: any = {
                            id: offering.id,
                            name: offering.name,
                        }
                        if (!product.showEnvironment) {
                            outOffering.regions = regions;
                        } else {
                            outOffering.environments = [];
                            if (offering.serviceEnvironments?.length) {
                                // Get active environments and deduplicate by grpId
                                const envMap = new Map();
                                offering.serviceEnvironments
                                    .filter((env: RegistryService) => env.active)
                                    .forEach((env: any) => {
                                        if (!envMap.has(env.grpId)) {
                                            envMap.set(env.grpId, { id: env.grpId, name: env.name });
                                        }
                                    });
                                outOffering.environments = Array.from(envMap.values());
                            }
                        }                       
                        productOfferings.push(outOffering);
                        return productOfferings;
                    }, [])

            } else {
                if (product.showEnvironment) {
                    if (product.serviceEnvironments?.length) {
                        result.environments = product.serviceEnvironments
                            .filter((environment: RegistryService) => environment.active)
                            .map((environment: any) => { return { id: environment.grpId, name: environment.name } })
                    }
                } else {
                    result.regions = regions;
                }
            }

            return result;
        });
    }

    function allProductsRegionAndEventSelected() {
        let eventTypes: any, regions: any, environments: any;
        if (globalEvent) {
            eventTypes = globalEventTypes.filter((eventType: any) => eventType.active);

            if (!eventTypes?.length) return false;

            regions = globalRegions.filter((region: any) => region.active);

            if (!regions?.length) return false;
        } else {
            for (const key in selectedProducts) {
                if (Object.prototype.hasOwnProperty.call(selectedProducts, key)) {
                    const product: RegistryProduct = selectedProducts[key];
                    eventTypes = product.eventTypes?.filter((eventType: any) => eventType.active);

                    if (!eventTypes?.length) return false;

                    if (product.showEnvironment) {
                        if (product.productOfferings?.length) {
                            let state = product.productOfferings.filter((offering: RegistryService) => offering.active)
                                .some((offering: RegistryService) => {
                                    return offering.serviceEnvironments?.some((environment: RegistryService) => environment.active)
                                });
                            if (!state) return false;
                        } else { // without offering
                            environments = product.serviceEnvironments?.filter((environment: RegistryService) => environment.active)
                            if (!environments?.length) return false;
                        }

                    } else {
                        regions = product.regions?.filter((region: any) => region.active);
                        if (!regions?.length) return false;
                    }
                }
            }
        }

        return true;

    }

    function gotoNextWizard() {
        if (activeWizard === PNSWIZARD.Product) {
            let _unsubscribeAll = isNoProductSelected() && isEdit();

            setUnsubscribeAll(_unsubscribeAll);
            if (_unsubscribeAll) {
                validateTimeZone(() => setActiveWizard(PNSWIZARD.Confirm));
            } else {
                setActiveWizard(PNSWIZARD.Region);
                let _hasEnvironments = hasEnvironments(),
                    _selectedProducts = getSelectedProducts();
                if (!isEdit()) {
                    setGlobalEvent(!_hasEnvironments);
                }
                setShowCustomRegions(_hasEnvironments);
                setSelectedProducts(_selectedProducts);
                window.scrollTo(0, 0);
            }
        }
        else {
            validateTimeZone(() => setActiveWizard(PNSWIZARD.Notification));
            window.scrollTo(0, 0);
        }

    }

    function isWizardCompleted(wizard: PNSWIZARD) {

        return (PNSWIZARD.Region === activeWizard && wizard === PNSWIZARD.Product) ||
            (PNSWIZARD.Notification === activeWizard && [PNSWIZARD.Product, PNSWIZARD.Region].includes(wizard)) ||
            (PNSWIZARD.Confirm === activeWizard && [PNSWIZARD.Product, PNSWIZARD.Region, PNSWIZARD.Notification].includes(wizard))

    }

    function isBizibleDataCenter(dataCenter: any): boolean {
        return (dataCenter.name.toLowerCase() === 'bizible')
    }

    function slackPreference() {
        let slackText = '', slackInfo = '';
        if (slackStatus !== SlackStatus.Installed && slackNotificationEnabled) {
            slackText = `${locales.slack_setup_pending}${slackConfirmInput ? ` (#${slackConfirmInput})` : ''}`;
            slackInfo = locales.slack_setup_pending_info;
        }

        if ((slackStatus === SlackStatus.Installed || slackStatus === SlackStatus.RevokePending) && !slackNotificationEnabled) {
            slackText = locales.slack_removal;
            slackInfo = locales.slack_removal_info;
        }
        if (slackNotificationEnabled && slackSubscriptions.channel) {
            slackText = slackSubscriptions.channel;
            slackInfo = locales.manage_subscription_confirm_preferances_page_slack_channel_icon_info;
        }

        if (slackText && !slackInfo) {
            return (
                <>
                    <div>{locales.slack}: <b>{slackText}</b></div>
                </>
            );
        } else if (slackText && slackInfo && slackNotificationEnabled && slackSubscriptions.channel) {
            var workspaceUrl = new URL(slackSubscriptions.workSpaceUrl)
            return (
                <>
                    <div>{locales.slack}: <a href={workspaceUrl.origin + '/channels/' + (slackText.startsWith('#') ? slackText.slice(1) : slackText)} rel="noreferrer" target="_blank" className="slack-channel-link"><b>{slackText.startsWith('#') ? slackText : `#${slackText}`}</b></a>                        <button 
                        onClick={() => {
                            setShowInfo({
                                ...showInfo,
                                slack: !showInfo.slack
                            })
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setShowInfo({
                                    ...showInfo,
                                    slack: !showInfo.slack
                                })
                            }
                        }}
                        className="info-icon"
                        aria-label={`${showInfo.slack ? 'Hide' : 'Show'} Slack channel information`}
                        aria-expanded={showInfo.slack}
                        style={{color: '#0D66D0'}}
                    ></button>
                    </div>
                    <div className={`slack-info ${showInfo.slack ? 'active' : ''}`}>{parse(slackInfo)}</div>
                </>
            )
        } else if (slackText && slackInfo) {
            return (
                <>
                    <div>{locales.slack}: <b>{slackText}</b>
                        <button 
                            onClick={() => {
                                setShowInfo({
                                    ...showInfo,
                                    slack: !showInfo.slack
                                })
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    setShowInfo({
                                        ...showInfo,
                                        slack: !showInfo.slack
                                    })
                                }
                            }}
                            className="info-icon"
                            aria-label={`${showInfo.slack ? 'Hide' : 'Show'} Slack information`}
                            aria-expanded={showInfo.slack}
                            style={{color: '#0D66D0'}}
                        ></button>
                    </div>
                    <div className={`slack-info ${showInfo.slack ? 'active' : ''}`}>{parse(slackInfo)}</div>
                </>
            )
        } else {
            return <></>;
        }
    }

    function teamsPreference() {
        let teamsText = '', teamsInfo = '';
        if (teamsStatus !== TeamsStatus.Installed && teamsNotificationEnabled) {
            teamsText = `${locales.teams_setup_pending}${teamsChannelInput ? ` (${teamsMicrosoftIdInput}, #${teamsChannelInput})` : ''}`;
            teamsInfo = locales.teams_setup_pending_info;
        }

        if(teamsStatus === TeamsStatus.Unapproved && teamsNotificationEnabled) {
            teamsText = `${locales.teams_setup_pending}${teamsChannelInput ? ` (${teamsMicrosoftIdInput}, #${teamsChannelInput})` : ''}`
            teamsInfo = locales.teams_unapproved_info;
        }

        if ((teamsStatus === TeamsStatus.Installed || teamsStatus === TeamsStatus.RevokePending) && !teamsNotificationEnabled) {
            teamsText = locales.teams_removal;
            teamsInfo = locales.teams_removal_info;
        }
        
        if (teamsNotificationEnabled && teamsSubscriptions) {
            if (teamsSubscriptions.length >= 1) {
                // Concatenate all conversation names with commas
                teamsText = teamsSubscriptions[0].conversationName;
                teamsInfo = locales.confirm_preferences_page_teams_channel_icon_info;
            } else{
                teamsInfo = locales.teams_setup_pending_info;
            }
        }

        if (teamsText && !teamsInfo) {
            return (
                <>
                    <div>{locales.teams}: <b>{teamsText}</b></div>
                </>
            );
        } else if (teamsText && teamsInfo) {
            return (
                <>
                    <div>{locales.teams}: <b>{teamsText}</b>
                        <button 
                            onClick={() => {
                                setShowInfo({
                                    ...showInfo,
                                    teams: !showInfo.teams
                                })
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    setShowInfo({
                                        ...showInfo,
                                        teams: !showInfo.teams
                                    })
                                }
                            }}
                            className="info-icon"
                            aria-label={`${showInfo.teams ? 'Hide' : 'Show'} Teams information`}
                            aria-expanded={showInfo.teams}
                            style={{color: '#0D66D0'}}
                        ></button>
                    </div>
                    <div className={`teams-info ${showInfo.teams ? 'active' : ''}`}>{parse(teamsInfo)}</div>
                </>
            )
        } else {
            return <></>;
        }
    }

    function renderSlackConfirm() {
        const handleKeyDown = (e: React.KeyboardEvent) => {
            // Prevent default tab behavior on modal container
            if (e.key === 'Tab') {
                e.preventDefault();
            }
        };

        const handleConfirm = () => {
            if (!slackConfirmInput.trim()) {
                setSlackConfirmError(true);
                return;
            }
            setShowSlackConfirm(false);
        };

        return (
            <>
                <div className="backdrop" onClick={() => setShowSlackConfirm(false)}></div>
                <div 
                    className="slack-confirm"
                    role="dialog"
                    aria-labelledby="slack-confirm-title"
                    aria-describedby="slack-confirm-content"
                    aria-modal="true"
                    onKeyDown={handleKeyDown}
                >
                    <div id="slack-confirm-title" className="slack-title" role="heading" aria-level={2}>{locales.slack_confirm_title}</div>
                    <div id="slack-confirm-content" className="content">
                        <ul>
                            <li>{locales.slack_confirm_select_channel}</li>
                            <li>{locales.slack_confirm_support}</li>
                            <li>{locales.slack_confirm_proceed}</li>
                        </ul>
                        <div className="mandatory-input-container">
                            <label htmlFor="slack-channel-input">{locales.slack_channel_select_text}</label>
                            
                            <input
                                id="slack-channel-input"
                                type="text"
                                className={`mandatory-input${slackConfirmError ? ' error' : ''}`}
                                placeholder="Enter channel name"
                                value={slackConfirmInput}
                                onChange={e => {
                                    setSlackConfirmInput(e.target.value);
                                    setSlackConfirmError(false);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleConfirm();
                                    }
                                }}
                                required
                                autoFocus
                                ref={slackConfirmInputRef}
                                aria-describedby={slackConfirmError ? "slack-error-msg" : undefined}
                            />
                            {slackConfirmError && <div id="slack-error-msg" className="input-error" aria-live="assertive">{locales.field_required}</div>}
                        </div>
                    </div>
                    <div className="buttons">
                        <button 
                            className='btn secondary' 
                            onClick={() => {
                                setShowSlackConfirm(false); 
                                setSlackNotificationEnabled(false);
                                // Focus back to the currently selected radio button after modal closes
                                setTimeout(() => {
                                    const noneRadioButton = document.querySelector('input[value="none"]') as HTMLInputElement;
                                    if (noneRadioButton) {
                                        noneRadioButton.focus();
                                    }
                                }, 100);
                            }}
                            ref={slackConfirmCancelRef}
                        >
                            Cancel
                        </button>
                        <button 
                            className='btn primary' 
                            onClick={handleConfirm}
                            ref={slackConfirmConfirmRef}
                        >
                            {locales.confirm_btn}
                        </button>
                    </div>
                </div>
            </>
        )
    }

    function renderTeamsConfirm() {
        const handleKeyDown = (e: React.KeyboardEvent) => {
            // Prevent default tab behavior on modal container
            if (e.key === 'Tab') {
                e.preventDefault();
            }
        };

        const handleConfirm = () => {
            let hasError = false;
            
            // Check Microsoft ID
            if (!teamsMicrosoftIdInput.trim()) {
                setTeamsMicrosoftIdError(true);
                hasError = true;
            } else if (!validateMicrosoftId(teamsMicrosoftIdInput.trim())) {
                setTeamsMicrosoftIdError(true);
                hasError = true;
            }
            // Check channel name
            if (!teamsChannelInput.trim()) {
                setTeamsChannelError(true);
                hasError = true;
            }
            if (hasError) {
                return;
            }
            
            setShowTeamsConfirm(false);
        };

        return (
            <>
                <div className="backdrop" onClick={() => {setShowTeamsConfirm(false); setTeamsNotificationEnabled(false)}}></div>
                <div 
                    className="teams-confirm"
                    role="dialog"
                    aria-labelledby="teams-confirm-title"
                    aria-describedby="teams-confirm-content"
                    aria-modal="true"
                    onKeyDown={handleKeyDown}
                >
                    <div id="teams-confirm-title" className="teams-title">{locales.teams_confirm_title}</div>
                    <div id="teams-confirm-content" className="content">
                        <ul>
                            <li>{locales.teams_confirm_guideline_1}</li>
                            <li>{locales.teams_confirm_guideline_2}</li>
                            <li>{parse(locales.teams_onboarding_prerequisite_new_channel)}</li>
                            <li>{locales.teams_confirm_guideline_3}</li>
                            <li>{locales.teams_confirm_guideline_4}</li>
                        </ul>
                        <div className="mandatory-input-container">
                            <label htmlFor="teams-microsoft-id-input">{locales.teams_microsoft_id_text}</label>
                            
                            <input
                                id="teams-microsoft-id-input"
                                type="email"
                                className={`mandatory-input${teamsMicrosoftIdError ? ' error' : ''}`}
                                placeholder="Enter Microsoft ID"
                                value={teamsMicrosoftIdInput}
                                onChange={e => {
                                    const value = e.target.value;
                                    setTeamsMicrosoftIdInput(value);
                                    setTeamsMicrosoftIdError(false);
                                    
                                    // Real-time validation
                                    //if (value.trim() && !validateMicrosoftId(value)) {
                                    //    setTeamsMicrosoftIdError(true);
                                    //}
                                }}
                                onBlur={e => {
                                    const value = e.target.value.trim();
                                    if (value && !validateMicrosoftId(value)) {
                                        setTeamsMicrosoftIdError(true);
                                    }
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleConfirm();
                                    }
                                }}
                                required
                                autoFocus
                            />
                            {teamsMicrosoftIdError && <div className="input-error" aria-live="assertive">{
                                !teamsMicrosoftIdInput.trim() 
                                    ? (locales.field_required || 'This field is required')
                                    : (locales.invalid_microsoft_id || 'Please enter a valid Microsoft email address')
                            }</div>}
                        </div>
                        <div className="mandatory-input-container">
                            <label htmlFor="teams-channel-input">{locales.teams_channel_select_text}</label>
                            
                            <input
                                id="teams-channel-input"
                                type="text"
                                className={`mandatory-input${teamsChannelError ? ' error' : ''}`}
                                placeholder="Enter channel name"
                                value={teamsChannelInput}
                                onChange={e => {
                                    setTeamsChannelInput(e.target.value);
                                    setTeamsChannelError(false);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleConfirm();
                                    }
                                }}
                                required
                            />
                            {teamsChannelError && <div className="input-error" aria-live="assertive">{locales.field_required}</div>}
                        </div>
                    </div>
                    <div className="buttons">
                        <button 
                            className='btn secondary' 
                            onClick={() => {
                                setShowTeamsConfirm(false);
                                // Uncheck the Teams checkbox
                                setTeamsNotificationEnabled(false);
                                // Focus back to the currently selected radio button after modal closes
                                setTimeout(() => {
                                    const noneRadioButton = document.querySelector('input[value="none"]') as HTMLInputElement;
                                    if (noneRadioButton) {
                                        noneRadioButton.focus();
                                    }
                                }, 100);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    setShowTeamsConfirm(false);
                                    setTeamsNotificationEnabled(false);
                                    // Focus back to the currently selected radio button after modal closes
                                    setTimeout(() => {
                                        const noneRadioButton = document.querySelector('input[value="none"]') as HTMLInputElement;
                                        if (noneRadioButton) {
                                            noneRadioButton.focus();
                                        }
                                    }, 100);
                                }
                            }}
                        >
                            Cancel
                        </button>
                        <button 
                            className='btn primary' 
                            onClick={handleConfirm}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleConfirm();
                                }
                            }}
                        >
                            {locales.confirm_btn}
                        </button>
                    </div>
                </div>
            </>
        )
    }

    function renderPreferences() {
        return (
            <div className="preference">
                {(activeWizard === PNSWIZARD.Confirm) ?
                    <>
                        <div>{locales.sb_email}: <b>{email}</b></div>
                        {slackPreference()}
                        {isTeamsEnabled? teamsPreference() : <></>}
                    </> : ''}
                <div>{locales.sb_lang}: <b>{userLanguage}</b>
                    <button 
                        onClick={() => {
                            setShowInfo({
                                ...showInfo,
                                language: !showInfo.language
                            })
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setShowInfo({
                                    ...showInfo,
                                    language: !showInfo.language
                                })
                            }
                        }}
                        className="info-icon"
                        aria-label={`${showInfo.language ? 'Hide' : 'Show'} language information`}
                        aria-expanded={showInfo.language}
                        style={{color: '#0D66D0'}}
                    ></button>
                </div>
                <div className={`lang-info ${showInfo.language ? 'active' : ''}`}>{parse(locales.lang_info)}</div>
                <div>{locales.timezone}: <b>{userTimezoneWithOffset}</b>
                    <button 
                        onClick={() => {
                            setShowInfo({
                                ...showInfo,
                                timeZome: !showInfo.timeZome
                            })
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setShowInfo({
                                    ...showInfo,
                                    timeZome: !showInfo.timeZome
                                })
                            }
                        }}
                        className="info-icon"
                        aria-label={`${showInfo.timeZome ? 'Hide' : 'Show'} timezone information`}
                        aria-expanded={showInfo.timeZome}
                        style={{color: '#0D66D0'}}
                    ></button></div>
                <div className={`timezone-info ${showInfo.timeZome ? 'active' : ''}`} >{parse(locales.timezone_info)}</div>
            </div>
        );
    }

    function renderConfirmTiles() {
        return (
            <div className="selected-products">
                {
                    selectedProducts.sort((a: any, b: any) => a.name.localeCompare(b.name)).map((product: RegistryProduct) => {
                        return (
                            <div key={`selected-${product.id}`} className="row">
                                <div className="col-md-4">
                                    <div className="product-name" role="heading" aria-level={4}>{product.name}</div>
                                    <span className="cloud-name">{getCloudName(product.id)}</span>
                                </div>
                                <div className="col-md-8">
                                    {
                                        product.productOfferings?.length ?
                                            <div className="product-offerings">
                                                <b>{locales.product_offering}: </b>
                                                {stringifyOffer(product.productOfferings)}
                                            </div> : ''
                                    }
                                    <div className="rigion-selected">
                                        <b>{locales.region_locations}: </b>
                                        {
                                            product.showEnvironment ?
                                                stringifyEnvironment(product) : stringifyRegion(product.regions)
                                        }
                                    </div>

                                    <div className="selected-events">
                                        <b>{locales.event_types}: </b>{joinEvents(product.eventTypes)}
                                    </div>
                                </div>
                            </div>
                        );
                    })
                }

            </div>
        );
    }


    function envToBsoMapping(productoroffering: any ) {

        let mappedEnv:any = [];
            productoroffering.serviceEnvironments?.forEach((environment: any) => {
                const matchedMapping = bsomapping.find((map:any) => map.bsoId === environment.id);
                environment.grpId = matchedMapping?.groupId || matchedMapping?.bsoId;
               
                mappedEnv.push(environment);
                
            })
        return mappedEnv.filter((map:any, index:any, self:any) =>
                index === self.findIndex((mapindex:any) => mapindex.grpId === map.grpId)
            );
    }
    function filterAndRenderOfferingsEnvironment(product: RegistryProduct) {
        let enviromentMap: any = {};
        if (product.productOfferings?.length) {
            return (
                <ul className={`region checkbox-group ${window.locale}`}>
                    {
                        product.productOfferings
                            .filter((productOffering: any) => productOffering.active)
                            .map((productOffering: any) => {
                                let filteredEnv = envToBsoMapping(productOffering);
                               
                                return filteredEnv?.map((environment: any) => {
                                   
                                    if (!enviromentMap[environment.grpId]) {
                                        enviromentMap[environment.grpId] = environment.grpId;
                                        return (
                                            <li key={`env-${environment.grpId}`} >
                                                <label className="checkbox-container">{environment.name}
                                                    <input 
                                                        type="checkbox"
                                                        tabIndex={0}
                                                        onKeyDown={e => {
                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                e.preventDefault();
                                                                environment.active = !environment.active;
                                                                setSelectedProducts([...selectedProducts]);
                                                            } else if (e.key === 'Tab' && !e.shiftKey) {
                                                                e.preventDefault();
                                
                                                                setTimeout(() => {
                                                                    navigateToEventsInCurrentProduct(e.target as HTMLElement);
                                                                }, 100);
                                                            }
                                                        }}
                                                        onChange={() => {
                                                            environment.active = !environment.active;
                                                            setSelectedProducts([...selectedProducts]);
                                                        }}
                                                        name={`environment${environment.grpId}[]`}
                                                        value={environment.grpId}
                                                        checked={!!environment.active}
                                                        aria-label={`${environment.name}, environment option, ${environment.active ? 'checked' : 'not checked'}`}
                                                    />
                                                    <span className="checkmark"></span>
                                                </label>
                                            </li>
                                        )

                                    }
                                });
                            })
                    }
                </ul>
            );

        } else {
            let filteredEnv = envToBsoMapping(product);
            return (
                <ul className={`region checkbox-group ${window.locale}`}>
                    {
                        
                        
                        filteredEnv?.map((environment: any) => {
                          
                            return (
                                <li key={`env-${environment.grpId}`} >
                                    <label className="checkbox-container">{environment.name}
                                        <input 
                                            type="checkbox"
                                            tabIndex={0}
                                            onKeyDown={e => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    environment.active = !environment.active;
                                                    setSelectedProducts([...selectedProducts]);
                                                } else if (e.key === 'Tab' && !e.shiftKey) {
                                                    e.preventDefault();
                                                    // Navigate to Events of THIS product (not next product)
                                                    setTimeout(() => {
                                                        navigateToEventsInCurrentProduct(e.target as HTMLElement);
                                                    }, 100);
                                                }
                                            }}
                                            onChange={() => {
                                                environment.active = !environment.active;
                                                setSelectedProducts([...selectedProducts]);
                                            }}
                                            name={`environment${environment.grpId}[]`}
                                            value={environment.grpId}
                                            checked={!!environment.active}
                                            aria-label={`${environment.name}, environment option, ${environment.active ? 'checked' : 'not checked'}`}
                                        />
                                        <span className="checkmark"></span>
                                    </label>
                                </li>
                            )
                        })
                    }
                </ul>
            )
        }
    }

    // Helper function to focus first checkbox when capability is expanded
    function focusFirstCheckboxInCapability(dataCenter: any) {
        setTimeout(() => {
            let firstCheckbox = null;
            
            if (isBizibleDataCenter(dataCenter)) {
                firstCheckbox = document.querySelector(`input[name="environment-bizible[]"]`);
            } else {
                const environmentContainer = document.querySelector(`.environment-region-list.active`);
                if (environmentContainer) {
                    firstCheckbox = environmentContainer.querySelector('input[type="checkbox"]');
                }
            }
            
            if (firstCheckbox) {
                (firstCheckbox as HTMLInputElement).focus();
            }
        }, 100);
    }

    // Helper function to navigate to events section of current product
    function navigateToEventsInCurrentProduct(currentElement: HTMLElement): boolean {
        const regionContainer = currentElement.closest('.region-container.custom');
        if (regionContainer) {
            const eventTypeDiv = regionContainer.querySelector('.event-type');
            if (eventTypeDiv) {
                const firstEventCheckbox = eventTypeDiv.querySelector('input[type="checkbox"]');
                if (firstEventCheckbox) {
                    (firstEventCheckbox as HTMLInputElement).focus();
                    return true;
                }
            }
        }
        return false;
    }

    // Helper function to handle capability toggle (expand/collapse)
    function handleCapabilityToggle(dataCenter: any, dataCenters: any[]) {
        let newState = !dataCenter.isOpen;
        dataCenters = hideAllDataCenters(dataCenters);
        dataCenter.isOpen = newState;
        
        setOpenCapabilities(prev => {
            const newSet = new Set(prev);
            if (newState) {
                newSet.add(dataCenter.name);
            } else {
                newSet.delete(dataCenter.name);
            }
            return newSet;
        });
        
        setSelectedProducts([...selectedProducts]);

        if (newState) {
            focusFirstCheckboxInCapability(dataCenter);
        }
    }

    // Helper function to navigate to next section (capability, region, or events)
    function navigateToNextSection() {
        setTimeout(() => {
            const allCapabilities = Array.from(document.querySelectorAll(`.environment-group-link`));
            const currentCapabilityElement = allCapabilities.find(cap => cap.classList.contains('active'));
            
            if (currentCapabilityElement) {
                const currentCapIndex = allCapabilities.indexOf(currentCapabilityElement);
                
                // Find next collapsed capability
                let nextCapability = null;
                for (let i = currentCapIndex + 1; i < allCapabilities.length; i++) {
                    if (!allCapabilities[i].classList.contains('active')) {
                        nextCapability = allCapabilities[i];
                        break;
                    }
                }
                
                if (nextCapability) {
                    (nextCapability as HTMLElement).focus();
                } else {
                    // No more capabilities within this product
                    // First try to find events section of CURRENT product
                    if (navigateToEventsInCurrentProduct(currentCapabilityElement as HTMLElement)) {
                        return;
                    }
                    
                    // If no events in current product, try region checkboxes of next product
                    const nextRegionCheckbox = document.querySelector(`ul[aria-labelledby*="regions-title"] input[type="checkbox"]`);
                    if (nextRegionCheckbox) {
                        (nextRegionCheckbox as HTMLInputElement).focus();
                    } else {
                        // Fallback: try to find any events
                        const firstEventCheckbox = document.querySelector(`input[type="checkbox"][aria-label*="grouped by events"]`);
                        if (firstEventCheckbox) {
                            (firstEventCheckbox as HTMLInputElement).focus();
                        }
                    }
                }
            }
        }, 0);
    }

    function renderEnvironment(product: RegistryProduct) {
        // Helper function to update environment across all offerings
        const updateEnvironmentAcrossOfferings = (environment: any, newActiveState: boolean) => {
            environment.active = newActiveState;
            
            // Update all offerings' environments that match by grpId
            product.productOfferings?.forEach((offering: any) => {
                offering.serviceEnvironments?.forEach((env: any) => {
                    if (!env.grpId) {
                        const mapping = bsomapping.find((m:any) => m.bsoId === env.id);
                        env.grpId = mapping?.groupId || env.id;
                    }
                    if (env.grpId === environment.grpId) {
                        env.active = newActiveState;
                    }
                });
            });
            
            setSelectedProducts([...selectedProducts]);
        };

        if (isMarketo(product) && product.productOfferings?.length) {
            let dataCenters: any = groupByDataCenter(product);
            return (
                <div className="environment-group environment">
                    <div className="region-info">
                        {locales.capabilities_info}
                    </div>
                    {
                        dataCenters.map((dataCenter: any, dindex: number) => {
                            return (
                                <div key={`datacenter-g-${dindex}`} className="groups">
                                    <span 
                                        className={`environment-group-link ${dataCenter.isOpen ? 'active' : ''}`} 
                                        onClick={() => handleCapabilityToggle(dataCenter, dataCenters)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                handleCapabilityToggle(dataCenter, dataCenters);
                                            }
                                        }}
                                        onFocus={() => setFocusedCapability(dataCenter.name)}
                                        tabIndex={openCapabilities.has(dataCenter.name) ? -1 : 0}
                                        role="button"
                                        aria-expanded={openCapabilities.has(dataCenter.name)}
                                        aria-label={`${dataCenter.name}, capability option, ${openCapabilities.has(dataCenter.name) ? 'expanded' : 'collapsed'}. Press Enter or Space to ${openCapabilities.has(dataCenter.name) ? 'collapse' : 'expand'} environments`}
                                    >
                                        {dataCenter.name}
                                    </span>
                                </div>
                            )
                        })
                    }
                    <div className="enviroment-regions-container">
                        {
                            dataCenters.map((dataCenter: any, index: number) => {
                                if (isBizibleDataCenter(dataCenter)) {
                                    return (
                                        <div key={`datacenter-v-${index}`} className={`environment-region-list ${dataCenter.isOpen ? 'active' : ''}`}>
                                            <div className="environment-group-title">
                                                {dataCenter.name}
                                            </div>
                                            <ul className="environment-region">
                                                <li >
                                                    <label className="checkbox-container">{ServiceRegions.Americas}
                                                        <input 
                                                            type="checkbox"
                                                            tabIndex={focusedCapability === dataCenter.name && openCapabilities.has(dataCenter.name) ? 0 : -1}
                                                            onKeyDown={e => {
                                                                if (e.key === 'Enter' || e.key === ' ') {
                                                                    e.preventDefault();
                                                                    let newState = !bizibleEnvironmentChecked;
                                                                    setBizibleEnvironmentChecked(newState);
                                                                    dataCenter.serviceEnvironments.forEach((environment: RegistryService) => {
                                                                        environment.active = newState;
                                                                    });
                                                                    setSelectedProducts([...selectedProducts]);
                                                                } else if (e.key === 'Tab' && !e.shiftKey) {
                                                                    navigateToNextSection();
                                                                }
                                                            }}
                                                            onChange={() => {
                                                                let newState = !bizibleEnvironmentChecked;
                                                                setBizibleEnvironmentChecked(newState);
                                                                dataCenter.serviceEnvironments.forEach((environment: RegistryService) => {
                                                                    environment.active = newState;
                                                                });
                                                                setSelectedProducts([...selectedProducts]);
                                                            }}
                                                            name={`environment-bizible[]`}
                                                            value={1}
                                                            checked={bizibleEnvironmentChecked}
                                                            aria-label={`${product.name} - ${dataCenter.name} - ${ServiceRegions.Americas}, environment option, ${bizibleEnvironmentChecked ? 'checked' : 'not checked'}`}
                                                        />
                                                        <span className="checkmark"></span>
                                                    </label>
                                                </li>
                                            </ul>
                                        </div>
                                    );
                                } else {
                                    let filteredEnv = envToBsoMapping(dataCenter);
                                    return (
                                        <div key={`datacenter-v-${index}`} className={`environment-region-list ${dataCenter.isOpen ? 'active' : ''}`}>
                                            <div className="environment-group-title">
                                                {dataCenter.name}
                                            </div>
                                            <ul className="environment-region">
                                                {
                                                   
                                                    filteredEnv
                                                        .map((environment: RegistryService) => {
                                                            environment.parsedName = parseEnvironmentName(environment.name)
                                                            return environment;
                                                        })
                                                        .sort((a: RegistryService, b: RegistryService) => a.parsedName?.localeCompare(b.parsedName || ''))
                                                        .map((environment: any) => {
                                                            return (
                                                                <li key={`env-g-${environment.id}`} >
                                                                    <label className="checkbox-container">{environment.parsedName}
                                                                        <input 
                                                                            type="checkbox"
                                                                            tabIndex={focusedCapability === dataCenter.name && openCapabilities.has(dataCenter.name) ? 0 : -1}
                                                                            onKeyDown={e => {
                                                                                if (e.key === 'Enter' || e.key === ' ') {
                                                                                    e.preventDefault();
                                                                                    updateEnvironmentAcrossOfferings(environment, !environment.active);
                                                                                } else if (e.key === 'Tab' && !e.shiftKey) {
                                                                                    const allCheckboxes = Array.from(document.querySelectorAll(`.environment-region-list.active input[type="checkbox"]`));
                                                                                    const currentIndex = allCheckboxes.indexOf(e.target as HTMLInputElement);
                                                                                    if (currentIndex === allCheckboxes.length - 1) {
                                                                                        navigateToNextSection();
                                                                                    }
                                                                                }
                                                                            }}
                                                                            onChange={() => {
                                                                                updateEnvironmentAcrossOfferings(environment, !environment.active);
                                                                            }}
                                                                            name={`environment${environment.grpId}[]`}
                                                                            value={environment.grpId}
                                                                            checked={!!environment.active}
                                                                            aria-label={`${product.name} - ${dataCenter.name} - ${environment.parsedName}, environment option, ${environment.active ? 'checked' : 'not checked'}`}
                                                                        />
                                                                        <span className="checkmark"></span>
                                                                    </label>
                                                                </li>
                                                            )
                                                        })
                                                }
                                            </ul>
                                        </div>
                                    );
                                }

                            })
                        }
                    </div>
                </div>
            )

        } else {
            return filterAndRenderOfferingsEnvironment(product);
        }

    }

    function renderRegion(product: RegistryProduct, productName?: string) {

        return (
            <ul aria-labelledby={`regions-title-${product.id}`}>
                {
                    product.regions?.map((region: any, regionIndex: number) => {
                        return (
                            <li key={`region-p-${region.id}`} >
                                <label 
                                    className="checkbox-container"
                                    tabIndex={-1}
                                    aria-label={`${region.name}, grouped by regions for ${productName}, ${region.active ? 'checked' : 'not checked'}`}
                                >
                                    {region.name}
                                    <input
                                        tabIndex={0}
                                        type="checkbox"
                                        checked={region.active}
                                        value={region.id}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                region.active = !region.active;
                                                setSelectedProducts([...selectedProducts]);
                                            } else if (e.key === 'Tab' && !e.shiftKey) {
                                                // Check if this is the last region checkbox for THIS product
                                                if (regionIndex === product.regions!.length - 1) {
                                                    e.preventDefault();
                                                    
                                                    // Navigate to Events section of the SAME product
                                                    setTimeout(() => {
                                                        if (!navigateToEventsInCurrentProduct(e.target as HTMLElement)) {
                                                            // Fallback if events not found in current product
                                                            navigateToNextSection();
                                                        }
                                                    }, 100);
                                                }
                                            }
                                        }}
                                        onChange={() => {
                                            region.active = !region.active;
                                            setSelectedProducts([...selectedProducts]);
                                        }}
                                        aria-label={`${region.name}, grouped by regions for ${productName}, ${region.active ? 'checked' : 'not checked'}`}
                                    />
                                    <span className="checkmark"></span>
                                </label>
                            </li>
                        );
                    })
                }
            </ul>
        );
    }

    function renderEventTypes(product: RegistryProduct, productName?: string) {
        return (
            <ul aria-labelledby={`events-title-${product.id}`}>
                {
                    product.eventTypes?.map((eventType: any) => {
                        return (
                            <li key={`event-p-${eventType.id}`} >
                                <label 
                                    className="checkbox-container"
                                    tabIndex={-1}
                                    aria-label={`${locales[eventType.name as keyof typeof locales]}, grouped by events for ${productName}, ${eventType.active ? 'checked' : 'not checked'}`}
                                >
                                    {locales[eventType.name as keyof typeof locales]}
                                    <input
                                        tabIndex={0}
                                        type="checkbox"
                                        checked={eventType.active}
                                        value={eventType.id}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                eventType.active = !eventType.active;
                                                setSelectedProducts([...selectedProducts]);
                                            }
                                        }}
                                        onFocus={e => {
                                            e.target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                                        }}
                                        onChange={() => {
                                            eventType.active = !eventType.active;
                                            setSelectedProducts([...selectedProducts]);
                                        }}
                                        aria-label={`${locales[eventType.name as keyof typeof locales]}, grouped by events for ${productName}, ${eventType.active ? 'checked' : 'not checked'}`}
                                    />
                                    <span className="checkmark"></span>
                                </label>
                            </li>
                        )
                    })
                }
            </ul>
        )
    }

    function renderGlobalSection() {
        return (
            <>
                <label
                    className="global-event"
                    htmlFor="global-event"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setGlobalEvent(true);
                        }
                    }}
                >
                    <input
                        id="global-event"
                        value="globalevent"
                        className="group-selection"
                        name="eventselection"
                        type="radio"
                        onChange={(e) => { setGlobalEvent(e.target.checked) }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setGlobalEvent(true);
                            }
                        }}
                        checked={globalEvent}
                        ref={globalEventRef}
                        tabIndex={0}
                    />
                    <span>{locales.region_global}</span>
                </label>
                <div className={`region-global-container ${globalEvent ? 'active' : ''}`}>
                    <div className="region-container global">
                        <div className="select-info">{locales.region_info_global}</div>
                        <div className="wrapper">
                            <fieldset className="regions-events-group">
                                <legend className="sr-only">Regions and Events Selection</legend>
                                <div className={`region checkbox-group ${window.locale}`}>
                                    <div className="title" id="regions-title">{locales.region_locations}</div>
                                    <ul aria-labelledby="regions-title">
                                        {
                                            globalRegions.map((region: any) => {
                                                return (
                                                    <li key={`region-g-${region.id}`}  >
                                                        <label 
                                                            className="checkbox-container"
                                                            tabIndex={-1}
                                                            aria-label={`${region.name}, grouped by regions, ${region.active ? 'checked' : 'not checked'}`}
                                                        >
                                                            {region.name}
                                                            <input
                                                                tabIndex={0}
                                                                type="checkbox"
                                                                checked={region.active}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                                        e.preventDefault();
                                                                        region.active = !region.active;
                                                                        setGlobalRegions([...globalRegions]);
                                                                    }
                                                                }}
                                                                onChange={() => {
                                                                    region.active = !region.active;
                                                                    setGlobalRegions([...globalRegions]);
                                                                }}
                                                                value={region.id}
                                                                aria-label={`${region.name}, grouped by regions, ${region.active ? 'checked' : 'not checked'}`}
                                                            />
                                                            <span className="checkmark"></span>
                                                        </label>
                                                    </li>
                                                );
                                            })
                                        }
                                    </ul>
                                </div>

                                <div className={`event-type ${window.locale}`}>
                                    <div className="title" id="events-title">{locales.event_types}</div>
                                    <ul aria-labelledby="events-title">
                                        {
                                            globalEventTypes.map((eventType: any) => {
                                                return (
                                                    <li key={`event-g-${eventType.id}`}>
                                                        <label 
                                                            className="checkbox-container"
                                                            tabIndex={-1}
                                                            aria-label={`${locales[eventType.name as keyof typeof locales]}, grouped by events, ${eventType.active ? 'checked' : 'not checked'}`}
                                                        >
                                                            {locales[eventType.name as keyof typeof locales]}
                                                            <input
                                                                tabIndex={0}
                                                                type="checkbox"
                                                                checked={eventType.active}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                                        e.preventDefault();
                                                                        eventType.active = !eventType.active;
                                                                        setGlobalEventTypes([...globalEventTypes]);
                                                                    }
                                                                }}
                                                                onChange={() => {
                                                                    eventType.active = !eventType.active;
                                                                    setGlobalEventTypes([...globalEventTypes]);
                                                                }}
                                                                value={eventType.id}
                                                                aria-label={`${locales[eventType.name as keyof typeof locales]}, grouped by events, ${eventType.active ? 'checked' : 'not checked'}`}
                                                            />
                                                            <span className="checkmark"></span>
                                                        </label>
                                                    </li>
                                                )
                                            })
                                        }
                                    </ul>
                                </div>
                            </fieldset>
                        </div>
                    </div>
                </div>
            </>
        )
    }

    function renderNotificationTab() {
        return (
            <div className={`pns-wizard-item ${PNSWIZARD.Notification} animate__animated animate__fadeIn active clearfix`}>
                <p id="communication-pref-label" className='communication-pref-title'>
                    {locales.communication_preference}
                </p>
                <div role="group" aria-labelledby="communication-pref-label">
                <label className="checkbox-container">
                    <span>{locales.sb_email}</span> <span className='mandatory_field'>*</span>
                    <input
                        type="checkbox"
                        checked={true}
                        disabled={true}
                        ref={emailCheckboxRef}
                    />
                    <span className="checkmark"></span>
                </label>
                <p className='communication-pref-title'>
                    {isTeamsEnabled && (
                        <>
                            {locales.communication_preference_channels}
                        </>
                    )}
                </p>
                {isTeamsEnabled ? (
                    // When Teams is enabled: Show all three as radio buttons (choose one)
                    <div 
                        style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', alignItems: 'flex-start' }}
                        role="radiogroup"
                        aria-label="Select notification channel preference. Choose None for no additional notifications, Slack to receive notifications in a Slack channel (will open setup popup), or Teams to receive notifications in a Teams channel (will open setup popup). You need to choose an existing Teams channel or create a new channel before you continue."
                    >
                        <label className="checkbox-container">
                            <span>{locales.none}</span>
                            <input
                                type="radio"
                                name="notificationMethod"
                                value="none"
                                checked={!slackNotificationEnabled && !teamsNotificationEnabled}
                                tabIndex={0}
                                ref={noneRadioRef}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        setSlackNotificationEnabled(false);
                                        setTeamsNotificationEnabled(false);
                                    } else if (e.key === 'ArrowRight') {
                                        e.preventDefault();
                                        setSlackNotificationEnabled(true);
                                        setTeamsNotificationEnabled(false);
                                    }
                                }}
                                onChange={(e) => {
                                    const isChecked = e.target.checked;
                                    if (isChecked) {
                                        setSlackNotificationEnabled(false);
                                        setTeamsNotificationEnabled(false);
                                    }
                                }}
                                aria-label="None, notification channel option, no notifications"
                            />
                            <span className="checkmark"></span>
                        </label>
                        <label className="checkbox-container">
                            <span>{locales.slack}</span>
                            <input
                                type="radio"
                                name="notificationMethod"
                                value="slack"
                                checked={slackNotificationEnabled}
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        setSlackNotificationEnabled(true);
                                        setTeamsNotificationEnabled(false);
                                        
                                        if (slackStatus === SlackStatus.None || slackStatus === SlackStatus.Revoked || slackStatus === SlackStatus.InstallPending) {
                                            setShowSlackConfirm(true);
                                        }
                                    } else if (e.key === 'ArrowLeft') {
                                        e.preventDefault();
                                        setSlackNotificationEnabled(false);
                                        setTeamsNotificationEnabled(false);
                                    } else if (e.key === 'ArrowRight') {
                                        e.preventDefault();
                                        setTeamsNotificationEnabled(true);
                                        setSlackNotificationEnabled(false);
                                    }
                                }}
                                onChange={(e) => {
                                    const isChecked = e.target.checked;
                                    setSlackNotificationEnabled(isChecked);
                                    
                                    // If slack is being selected, uncheck teams
                                    if (isChecked) {
                                        setTeamsNotificationEnabled(false);
                                    }
                                    
                                    if (isChecked && (slackStatus === SlackStatus.None || slackStatus === SlackStatus.Revoked || slackStatus === SlackStatus.InstallPending)) {
                                        setShowSlackConfirm(true);
                                    }
                                }}
                                ref={slackCheckboxRef}
                                aria-label={`${locales.slack}, notification channel option, ${slackNotificationEnabled ? 'selected' : 'not selected'}. Selecting this option will open a setup modal`}
                            />
                            <span className="checkmark"></span>
                        </label>
                        <label className="checkbox-container">
                            <span>{locales.teams}</span>
                            <input
                                type="radio"
                                name="notificationMethod"
                                value="teams"
                                checked={teamsNotificationEnabled}
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        setTeamsNotificationEnabled(true);
                                        setSlackNotificationEnabled(false);
                                        
                                        if (teamsStatus === TeamsStatus.None || teamsStatus === TeamsStatus.Revoked || teamsStatus === TeamsStatus.InstallPending || teamsStatus === TeamsStatus.Unapproved) {
                                            setDefaultMicrosoftId();
                                            setShowTeamsConfirm(true);
                                        }
                                    } else if (e.key === 'ArrowLeft') {
                                        e.preventDefault();
                                        setSlackNotificationEnabled(true);
                                        setTeamsNotificationEnabled(false);
                                    }
                                }}
                                onChange={(e) => {
                                    const isChecked = e.target.checked;
                                    setTeamsNotificationEnabled(isChecked);
                                    // If teams is being selected, uncheck slack
                                    if (isChecked) {
                                        setSlackNotificationEnabled(false);
                                    }

                                    if (isChecked && (teamsStatus === TeamsStatus.None || teamsStatus === TeamsStatus.Revoked || teamsStatus === TeamsStatus.InstallPending || teamsStatus === TeamsStatus.Unapproved)) {
                                        setDefaultMicrosoftId(); // Set default Microsoft ID
                                        
                                        setShowTeamsConfirm(true);
                                    }
                                    
                                }}
                                aria-label={`${locales.teams}, notification channel option, ${teamsNotificationEnabled ? 'selected' : 'not selected'}`}
                            />
                            <span className="checkmark"></span>
                        </label>
                    </div>
                ) : (
                    // When Teams is disabled: Show only Slack as checkbox (can be checked/unchecked)
                    <label className="checkbox-container">
                        <span>{locales.slack}</span>
                        <input
                            type="checkbox"
                            checked={slackNotificationEnabled}
                            onChange={(e) => {
                                const isChecked = e.target.checked;
                                setSlackNotificationEnabled(isChecked);
                                
                                if (isChecked && (slackStatus === SlackStatus.None || slackStatus === SlackStatus.Revoked || slackStatus === SlackStatus.InstallPending)) {
                                    setShowSlackConfirm(true);
                                }
                            }}
                            aria-label={`${locales.slack}, notification channel option, ${slackNotificationEnabled ? 'checked' : 'not checked'}. Selecting this option will open a setup modal`}
                        />
                        <span className="checkmark"></span>
                    </label>
                )}
                </div>

                {renderPreferences()}

                <div className="buttons pull-right">
                    <button className={`btn primary continue-to-confirm ${loading ? 'loading' : ''}`} disabled={loading} onClick={() => {
                        gotoConfrim();
                    }} style={loading ? { '--hover-bg-color': '#1A4D8F', '--spinner-color': '#FFFFFF' } as React.CSSProperties : { '--hover-bg-color': '#1A4D8F' } as React.CSSProperties}>{locales.continue_txt}{loading ? <Spinner isButton={true} /> : ''}</button>
                    <button className="btn secondary" onClick={() => {
                        setActiveWizard(PNSWIZARD.Region);
                        window.scrollTo(0, 0);
                    }}>{locales.back_txt}</button>
                    <button className="btn secondary" onClick={() => {
                        OMEGA.init().updateAndTrack("event", [{
                            ...eventDefaultValue,
                            element: "Cancelled subscription",
                            attributes: {
                                cancelledFrom: 'region'
                            }
                        }]);
                        histroyAPI.push("/proactive-notifications/manage");

                    }}>{locales.cancel_txt}</button>

                </div>
            </div>
        );
    }

    function renderRegionsTab() {
        return (
            <div className={`pns-wizard-item ${PNSWIZARD.Region} animate__animated animate__fadeIn active clearfix`}>
                {!showCustomRegions ? renderGlobalSection() : ''}
                <label 
                    className="custom-event" 
                    htmlFor="custom-event"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setGlobalEvent(false);
                        }
                    }}
                >
                    <input
                        className="group-selection"
                        onChange={(e) => { setGlobalEvent(!e.target.checked) }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setGlobalEvent(false);
                            }
                        }}
                        name="eventselection"
                        value="customevent"
                        id="custom-event"
                        type="radio"
                        checked={!globalEvent}
                        ref={customEventRef}
                        tabIndex={0}
                    />
                    <span>{locales.region_customize}</span>
                </label>
                {!globalEvent && (
                    <div className={`region-custom-container ${!globalEvent ? 'active' : ''}`}>
                        <div className="select-info">{locales.region_info}</div>
                        {
                            selectedProducts.map((product: RegistryProduct) => {
                                return (
                                    <div key={`product-custom-${product.id}`} className={`region-container custom service-${product.id}`}>
                                        <div className="product"role="heading" aria-level={3}>{product.name}</div>
                                        <div className={`region ${window.locale}`}>
                                            <div className="title" id={`regions-title-${product.id}`}>{locales.region_locations}</div>
                                            {
                                                product.showEnvironment ? renderEnvironment(product) : renderRegion(product,product.name)
                                            }
                                        </div>
                                        <div className={`event-type ${window.locale}`}>
                                            <div className="title" id={`events-title-${product.id}`}>{locales.event_types}</div>
                                            {renderEventTypes(product,product.name)}
                                        </div>
                                    </div>
                                )
                            })
                        }
                    </div>
                )}
                <div className="buttons pull-right">
                    <button
                        className={`btn primary continue-to-region ${loading ? 'loading' : ''}`}
                        disabled={loading || !allProductsRegionAndEventSelected()}
                        onClick={() => { gotoNextWizard(); }}
                        ref={continueBtnRef}
                        style={loading ? { '--hover-bg-color': '#1A4D8F', '--spinner-color': '#FFFFFF' } as React.CSSProperties : { '--hover-bg-color': '#1A4D8F' } as React.CSSProperties}
                    >
                        {locales.continue_txt}{loading ? <Spinner isButton={true} /> : ''}
                    </button>
                    <button
                        className="btn secondary"
                        onClick={() => {
                            setActiveWizard(PNSWIZARD.Product);
                            window.scrollTo(0, 0);
                        }}
                        ref={backBtnRef}
                    >
                        {locales.back_txt}
                    </button>
                    <button
                        className="btn secondary"
                        onClick={() => {
                            OMEGA.init().updateAndTrack("event", [{
                                ...eventDefaultValue,
                                element: "Cancelled subscription",
                                attributes: {
                                    cancelledFrom: 'region'
                                }
                            }]);
                            histroyAPI.push("/proactive-notifications/manage");
                        }}
                        ref={cancelBtnRef}
                    >
                        {locales.cancel_txt}
                    </button>
                </div>
            </div>
        )
    }

    function renderProductTab() {
       
        return (
            <div className={`pns-wizard-item ${PNSWIZARD.Product} animate__animated animate__fadeIn active clearfix`}>
                <div attt-data={entitlements.length} className={entitlements.length ? "entitlement-message show" : "entitlement-message"}>{locales.entitlement_message}</div>
                <div className="row">
                    {cloudList.map((cloud: RegistryCloud, index: number) => {
                        return (
                            <div key={`cloud-${cloud.id}`} className="col cloud-products">
                                <button
                                    {...(cloud.id === CLOUDIDS.ADOBE_CLOUD_PLATFORM_ID) ? { 
                                        "data-tip": cloud.name,
                                        "data-for": `cloud-tip-${cloud.id}`,
                                        "data-event": "mouseenter focus",
                                        "data-event-off": "mouseleave blur"
                                    } : {}}
                                    tabIndex={0}
                                    aria-label={cloud.isOpen ? `Collapse ${cloud.name}` : `Expand ${cloud.name}`}
                                    aria-expanded={cloud.isOpen}
                                    onClick={(e) => { cloud.isOpen = !cloud.isOpen; setCloudList([...cloudList]); ReactTooltip.rebuild(); }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            cloud.isOpen = !cloud.isOpen;
                                            setCloudList([...cloudList]);
                                            ReactTooltip.rebuild();
                                        }
                                    }}
                                    onFocus={(e) => {
                                        e.target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                                    }}
                                    className={cloud.isOpen ? 'title checkbox-container active' : 'title checkbox-container'}
                                >
                                    {cloud.name}
                                </button>

                                <div className="product-list">
                                    {
                                        cloud.cloudProducts.map((product: RegistryProduct) => {
                                            if (product.productOfferings?.length) {
                                                return (
                                                    <div key={`product-${product.id}`} className={product.isOpen ? 'product-container has-offering active' : 'product-container has-offering'}>
                                                        <button
                                                            className="expand"
                                                            tabIndex={0}
                                                            aria-expanded={product.isOpen}
                                                            aria-label={product.isOpen ? `Collapse ${product.name}` : `Expand ${product.name}`}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                product.isOpen = !product.isOpen;
                                                                setCloudList([...cloudList]);
                                                            }}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter' || e.key === ' ') {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    product.isOpen = !product.isOpen;
                                                                    setCloudList([...cloudList]);
                                                                }
                                                            }}
                                                            // style={{ minWidth: '24px', minHeight: '24px', padding: '5px' }}
                                                        ></button>
                                                        <label
                                                            tabIndex={-1}
                                                            className={`checkbox-container product-${product.id} ${product.partial ? 'partial' : ''} product-label service-offer-label`}
                                                        >
                                                            <span className="text" id={`product-label-${product.id}`}>{product.name}</span>
                                                            <input
                                                                tabIndex={0}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                                        e.preventDefault();
                                                                        product.active = !product.active;
                                                                        product.partial = false;
                                                                        product.isOpen = true;
                                                                        if (product.productOfferings) {
                                                                            product.productOfferings = product.productOfferings.map((offer: RegistryService) => {
                                                                                offer.active = product.active;
                                                                                return offer;
                                                                            });
                                                                        }
                                                                        setCloudList([...cloudList]);
                                                                    }
                                                                }}
                                                                onFocus={(e) => {
                                                                    e.target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                                                                }}
                                                                onChange={(e) => {
                                                                    product.active = !product.active;
                                                                    product.partial = false;
                                                                    product.isOpen = true;
                                                                    if (product.productOfferings) {
                                                                        product.productOfferings = product.productOfferings.map((offer: RegistryService) => {
                                                                            offer.active = product.active;
                                                                            return offer;
                                                                        });
                                                                    }
                                                                    setCloudList([...cloudList]);
                                                                }}
                                                                data-offer-length="{{serviceOfferings.length}}" data-has-offer="true" type="checkbox"
                                                                name={`cloud${cloud.id}[]`}
                                                                value={product.id}
                                                                checked={!!product.active}
                                                                aria-checked={product.partial ? 'mixed' : (product.active ? 'true' : 'false')}
                                                                aria-label={`${product.name}, product selection, ${product.partial ? 'partially checked' : (product.active ? 'checked' : 'not checked')}`}
                                                            />
                                                            <span className="checkmark"></span>
                                                        </label>
                                                        <div className="service-offerings ">
                                                            <ul role="group" aria-labelledby={`product-label-${product.id}`}>
                                                                {
                                                                    product.productOfferings.map((offering: RegistryService) => {
                                                                        return (
                                                                            <li key={`offering-${offering.id}`} >
                                                                                <label
                                                                                    tabIndex={-1}
                                                                                    className={`checkbox-container offer-label offer-${offering.id}`}
                                                                                >
                                                                                    {offering.name}
                                                                                    <input
                                                                                        tabIndex={0}
                                                                                        onKeyDown={(e) => {
                                                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                                                e.preventDefault();
                                                                                                offering.active = !offering.active;
                                                                                                let selectedOffers = product.productOfferings?.filter((offer: RegistryService) => offer.active);
                                                                                                if ((selectedOffers && selectedOffers.length > 0) && (selectedOffers?.length !== product.productOfferings?.length)) {
                                                                                                product.partial = true;
                                                                                            } else {
                                                                                                product.partial = false;
                                                                                            }
                                                                                            product.active = !!selectedOffers?.length;
                                                                                            setCloudList([...cloudList]);
                                                                                        }
                                                                                    }}
                                                                                    onFocus={(e) => {
                                                                                        e.target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                                                                                    }}
                                                                                    onChange={(e) => {
                                                                                        e.stopPropagation();
                                                                                        offering.active = !offering.active;
                                                                                        let selectedOffers = product.productOfferings?.filter((offer: RegistryService) => offer.active);
                                                                                        if ((selectedOffers && selectedOffers.length > 0) && (selectedOffers?.length !== product.productOfferings?.length)) {
                                                                                            product.partial = true;
                                                                                        } else {
                                                                                            product.partial = false;
                                                                                        }
                                                                                        product.active = !!selectedOffers?.length;
                                                                                        setCloudList([...cloudList]);
                                                                                    }}
                                                                                    className={`offer-${offering.id}`} type="checkbox"
                                                                                    name={`product${product.id}`} value={offering.id}
                                                                                    checked={!!offering.active}
                                                                                    aria-checked={offering.active ? 'true' : 'false'}
                                                                                    aria-label={`${offering.name}, offering for ${product.name}, ${offering.active ? 'checked' : 'not checked'}`}
                                                                                />
                                                                                    <span className="checkmark"></span>
                                                                                </label>
                                                                            </li>
                                                                        )
                                                                    })
                                                                }
                                                            </ul>
                                                        </div>
                                                    </div>
                                                );
                                            } else {
                                                return (
                                                    <div key={`product-${product.id}`} className="product-container no-offering">
                                                        <label 
                                                            tabIndex={-1}
                                                            className={`checkbox-container product-label product-${product.id}`}
                                                        >
                                                            {product.name}
                                                            <input
                                                                tabIndex={0}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                                        e.preventDefault();
                                                                        product.active = !product.active;
                                                                        setCloudList([...cloudList]);
                                                                    }
                                                                }}
                                                                onFocus={(e) => {
                                                                    e.target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                                                                }}
                                                                onChange={() => {
                                                                    product.active = !product.active;
                                                                    setCloudList([...cloudList]);
                                                                }} type="checkbox"
                                                                name={`cloud${cloud.id}[]`}
                                                                value={product.id}
                                                                checked={!!product.active}
                                                                aria-checked={product.partial ? 'mixed' : (product.active ? 'true' : 'false')}
                                                                aria-label={`${product.name}, product selection, ${product.partial ? 'partially checked' : (product.active ? 'checked' : 'not checked')}`}
                                                            />
                                                            <span className="checkmark"></span>
                                                        </label>
                                                    </div>
                                                )
                                            }
                                        })
                                    }
                                </div>
                            </div>
                        );
                    })}
                </div>
                <ReactTooltip 
                    id={`cloud-tip-${CLOUDIDS.ADOBE_CLOUD_PLATFORM_ID}`}
                    clickable
                    delayHide={200}
                    afterShow={() => {}}
                    afterHide={() => {}}
                />

                <div className={`select-product-message ${isCloudOpened() ? 'hide' : ''}`}>
                    <p>{locales.click_to_select_products}</p>
                </div>
                <div className="buttons pull-right">
                    <button
                        className={`btn primary continue-to-region ${loading ? 'loading' : ''}`}
                        disabled={disableButton() || loading}
                        onClick={() => {
                            gotoNextWizard();
                        }}
                        style={loading ? { '--hover-bg-color': '#1A4D8F', '--spinner-color': '#FFFFFF' } as React.CSSProperties : { '--hover-bg-color': '#1A4D8F' } as React.CSSProperties}
                    >{locales.continue_txt} {loading ? <Spinner isButton={true} /> : ''}</button>
                    <button className="btn secondary" onClick={() => {
                        OMEGA.init().updateAndTrack("event", [{
                            ...eventDefaultValue,
                            element: "Cancelled subscription",
                            attributes: {
                                cancelledFrom: 'product'
                            }
                        }]);
                        histroyAPI.push("/proactive-notifications/manage")
                    }} >{locales.cancel_txt}</button>

                </div>
            </div>
        )
    }

    function renderConfirmTab() {

        const getConfirmButtonOptions = () => {
            const slackWasInstalled = initialSlackEnabled;
            const teamsWasInstalled = isTeamsEnabled ? initialTeamsEnabled : false;
            const slackIsSelected = slackNotificationEnabled;
            const teamsIsSelected = isTeamsEnabled ? teamsNotificationEnabled : false;

            // Neither installed, Both NOT selected
            if (!slackWasInstalled && !teamsWasInstalled && !slackIsSelected && !teamsIsSelected) {
                return [{ text: locales.confirm_preferences, primary: true, action: () => subscribe() }];
            }

            // Neither installed, Teams selected
            if (!slackWasInstalled && !teamsWasInstalled && !slackIsSelected && teamsIsSelected) {
                return [{ text: locales.confirm_and_setup_teams, primary: true, action: () => subscribe({ teamsAction: AppInstallAction.Setup }) }];
            }

            // Neither installed, Slack selected
            if (!slackWasInstalled && !teamsWasInstalled && slackIsSelected && !teamsIsSelected) {
                return [{ text: locales.confirm_and_setup_slack, primary: true, action: () => subscribe({ slackAction: AppInstallAction.Setup }) }];
            }
            // Neither installed, Both selected
            if (!slackWasInstalled && !teamsWasInstalled && slackIsSelected && teamsIsSelected) {
                return [{ text: locales.confirm_and_setup_notifications, primary: true, action: () => subscribe({ slackAction: AppInstallAction.Setup, teamsAction: AppInstallAction.Setup }) }];
            }
            // Slack installed, Teams not. Slack removed, Teams NOT added.
            if (slackWasInstalled && !teamsWasInstalled && !slackIsSelected && !teamsIsSelected) {
                return [{ text: locales.confirm_and_remove_slack_btn, primary: true, action: () => subscribe({ slackAction: AppInstallAction.Revoke }) }];
            }
            // Slack installed, Teams not. Slack selected, Teams NOT selected
            if (slackWasInstalled && !teamsWasInstalled && slackIsSelected && !teamsIsSelected) {
                return [
                    { text: locales.confirm_preferences, primary: true, action: () => subscribe() },
                    { text: locales.confirm_preferences_and_change_slack_channel_button, primary: true, action: () => subscribe({ slackAction: AppInstallAction.Modify }) }
                ];
            }
            // Slack installed, Teams not. Slack selected, Teams ADDED.
            if (slackWasInstalled && !teamsWasInstalled && slackIsSelected && teamsIsSelected) {
                return [{ text: locales.confirm_and_setup_notifications, primary: true, action: () => subscribe({ slackAction: AppInstallAction.Modify, teamsAction: AppInstallAction.Setup }) }];
            }
            // Slack installed, Teams not. Slack removed, Teams added.
            if (slackWasInstalled && !teamsWasInstalled && !slackIsSelected && teamsIsSelected) {
                return [{ text: locales.confirm_and_setup_teams, primary: true, action: () => subscribe({ slackAction: AppInstallAction.Revoke, teamsAction: AppInstallAction.Setup }) }];
            }
            // Teams installed, slack not. Teams removed, Slack NOT added
            if (!slackWasInstalled && teamsWasInstalled && !slackIsSelected && !teamsIsSelected) {
                return [{ text: locales.confirm_and_remove_teams, primary: true, action: () => subscribe({ teamsAction: AppInstallAction.Revoke }) }];
            }
            // Teams installed, Slack not. Teams selected, Slack ADDED.
            if (!slackWasInstalled && teamsWasInstalled && slackIsSelected && teamsIsSelected) {
                return [{ text: locales.confirm_and_setup_notifications, primary: true, action: () => subscribe({ slackAction: AppInstallAction.Setup, teamsAction: AppInstallAction.Modify }) }];
            }
            // Teams installed, slack not. Teams removed, Slack added
            if (!slackWasInstalled && teamsWasInstalled && slackIsSelected && !teamsIsSelected) {
                return [{ text: locales.confirm_and_setup_slack, primary: true, action: () => subscribe({ slackAction: AppInstallAction.Setup, teamsAction: AppInstallAction.Revoke }) }];
            }

            // Teams installed, slack not. Teams selected, Slack NOT added
            if (!slackWasInstalled && teamsWasInstalled && !slackIsSelected && teamsIsSelected) {
                return [
                    { text: locales.confirm_preferences, primary: true, action: () => subscribe() }
                ];
            }

            // Both installed, Both selected - not possible with current single channel setup
            if (slackWasInstalled && teamsWasInstalled && slackIsSelected && teamsIsSelected) {
                return [
                    { text: locales.confirm_preferences, primary: true, action: () => subscribe() },
                    { text: locales.confirm_preferences_and_modify_notification_setup, primary: false, action: () => subscribe({ slackAction: AppInstallAction.Modify, teamsAction: AppInstallAction.Modify }) }
                ];
            }
            if (slackWasInstalled && teamsWasInstalled && !slackIsSelected && teamsIsSelected) {
                return [{ text: locales.confirm_preferences, primary: true, action: () => subscribe({
                    slackAction: AppInstallAction.Revoke,
                    teamsAction: AppInstallAction.None
                }) }];
            }
            if (slackWasInstalled && teamsWasInstalled && slackIsSelected && !teamsIsSelected) {
                return [
                    { text: locales.confirm_preferences, primary: true, action: () => subscribe() },
                    { text: locales.confirm_preferences_and_change_slack_channel_button, primary: true, action: () => subscribe({slackAction: AppInstallAction.Modify, teamsAction: AppInstallAction.None}) }
                ];
            }
            /*// Row 12: Both installed, One of them unselected
            if (slackWasInstalled && teamsWasInstalled && (slackIsSelected !== teamsIsSelected)) {
                const removeText = !slackIsSelected ? locales.confirm_and_remove_slack_btn : locales.confirm_and_remove_teams;
                return [
                    { text: removeText, primary: true, action: () => subscribe({ 
                        slackAction: !slackIsSelected ? AppInstallAction.Revoke : AppInstallAction.None, 
                        teamsAction: !teamsIsSelected ? AppInstallAction.Revoke : AppInstallAction.None 
                    }) },
                    { text: locales.confirm_preferences_and_modify_notification_setup, primary: false, action: () => subscribe({
                        slackAction: !slackIsSelected ? AppInstallAction.Revoke : AppInstallAction.Modify, 
                        teamsAction: !teamsIsSelected ? AppInstallAction.Revoke : AppInstallAction.Modify 
                     }) }
                ];
            }*/
            // Row 13: Both installed, Both unselected - when teams channel has not been removed and slack channel was setup and now being removed
            if (slackWasInstalled && teamsWasInstalled && !slackIsSelected && !teamsIsSelected) {
                return [{ text: locales.confirm_and_remove_slack_btn, primary: true, action: () => subscribe({ slackAction: AppInstallAction.Revoke, teamsAction: AppInstallAction.None }) }];
            }

            // Default catch-all, though it shouldn't be reached if the table is exhaustive.
            return [{ text: locales.confirm_preferences, primary: true, action: () => subscribe() }];
        };

        const buttonsToShow = getConfirmButtonOptions().reverse();


        return (
            <div className="pns-wizard-item confirm active animate__animated clearfix">
                {
                    unsubscribeAll ?
                        <>
                            <h3>{locales.no_subscriptions}</h3>
                            <div>{locales.no_product_info}</div>
                            <div>{locales.no_product_action_info}</div>
                        </> : <h3>{locales.confirm_subscription_title}</h3>
                }
                {renderPreferences()}
                {unsubscribeAll ? '' : renderConfirmTiles()}
                {showAPIError()}
                <div className="buttons pull-right">
                    {buttonsToShow.map((btn, index) => (
                        <button 
                            key={index} 
                            ref={btn.primary ? confirmPrimaryBtnRef : null}
                            className={`btn ${btn.primary ? 'primary' : 'secondary'} continue-to-confirm ${loading ? 'loading' : ''}`} 
                            disabled={loading || apiError} 
                            onClick={btn.action}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    if (!loading && !apiError) {
                                        btn.action();
                                    }
                                }
                            }}
                            aria-label={`${btn.text}${loading ? ', loading' : ''}${apiError ? ', disabled due to error' : ''}`}
                            style={loading && btn.primary ? { '--hover-bg-color': '#1A4D8F', '--spinner-color': '#FFFFFF' } as React.CSSProperties : btn.primary ? { '--hover-bg-color': '#1A4D8F' } as React.CSSProperties : undefined}
                        >
                            {btn.text}{loading && btn.primary ? <Spinner isButton={true} /> : ''}
                        </button>
                    ))}
                    <button 
                        className="btn secondary" 
                        onClick={() => {
                            setActiveWizard(unsubscribeAll ? PNSWIZARD.Product : PNSWIZARD.Notification);
                            window.scrollTo(0, 0);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setActiveWizard(unsubscribeAll ? PNSWIZARD.Product : PNSWIZARD.Notification);
                                window.scrollTo(0, 0);
                            }
                        }}
                        aria-label={`${locales.back_txt}, go to previous step`}
                    >
                        {locales.back_txt}
                    </button>
                    <button 
                        className="btn secondary" 
                        onClick={() => {
                            OMEGA.init().updateAndTrack("event", [{
                                ...eventDefaultValue,
                                element: "Cancelled subscription",
                                attributes: {
                                    cancelledFrom: 'confirm'
                                }
                            }]);
                            histroyAPI.push("/proactive-notifications/manage")
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                OMEGA.init().updateAndTrack("event", [{
                                    ...eventDefaultValue,
                                    element: "Cancelled subscription",
                                    attributes: {
                                        cancelledFrom: 'confirm'
                                    }
                                }]);
                                histroyAPI.push("/proactive-notifications/manage");
                            }
                        }}
                        aria-label={`${locales.cancel_txt}, cancel subscription setup`}
                    >
                        {locales.cancel_txt}
                    </button>
                </div>

            </div>
        );
    }

    function renderPNSWizard() {
        if (pageLoading) {
            return <>{HideFooter(true)}<Spinner /></>
        } else {
            HideFooter(false)
            if (activeWizard === PNSWIZARD.Product) {
                return renderProductTab();
            } else if (activeWizard === PNSWIZARD.Region) {
                return renderRegionsTab();
            } else if (activeWizard === PNSWIZARD.Notification) {
                return renderNotificationTab();
            } else {
                return renderConfirmTab();
            }
        }

    }


    useEffect(() => {
        if (
            activeWizard === PNSWIZARD.Notification && slackNotificationEnabled &&
            (
                slackStatus === SlackStatus.None ||
                slackStatus === SlackStatus.Revoked ||
                slackStatus === SlackStatus.InstallPending
            )
        ) {
            setShowSlackConfirm(true);
        }
    }, [slackNotificationEnabled, slackStatus, activeWizard]);

    useEffect(() => {
        if (
            activeWizard === PNSWIZARD.Notification && teamsNotificationEnabled &&
            (
                teamsStatus === TeamsStatus.None ||
                teamsStatus === TeamsStatus.Revoked ||
                teamsStatus === TeamsStatus.InstallPending ||
                teamsStatus === TeamsStatus.Unapproved 
            )
        ) {
            setDefaultMicrosoftId();
            
            setShowTeamsConfirm(true);
        }
    }, [teamsNotificationEnabled, teamsStatus, activeWizard]);


    function getMicrosoftEmailId() {
        console.log('Checking for Microsoft email ID...');
        
        // Ensure MSAL is initialized
        initializeMSAL();
        
        // 1. Check for Microsoft Graph API session (if using MSAL)
        try {
            // Check if Microsoft Authentication Library (MSAL) is available
            if (window.msal) {
                const accounts = window.msal.getAllAccounts();
                if (accounts && accounts.length > 0) {
                    return accounts[0].username; // This is usually the email
                }
            }
        } catch (e) {
            // MSAL not available, continue
        }

        // 2. Check for Office 365 authentication tokens in localStorage
        try {
            const keys = Object.keys(localStorage);
            for (let key of keys) {
                if (key.includes('msal') || key.includes('microsoft') || key.includes('office') || key.includes('outlook')) {
                    const value = localStorage.getItem(key);
                    if (value) {
                        try {
                            const parsed = JSON.parse(value);
                            // Look for email in various possible structures
                            if (parsed.username) return parsed.username;
                            if (parsed.account?.username) return parsed.account.username;
                            if (parsed.idTokenClaims?.preferred_username) return parsed.idTokenClaims.preferred_username;
                            if (parsed.idTokenClaims?.email) return parsed.idTokenClaims.email;
                        } catch (parseError) {
                            // Continue if JSON parsing fails
                        }
                    }
                }
            }
        } catch (e) {
            // Continue if localStorage access fails
        }

        // 3. Check for Microsoft authentication cookies
        try {
            const cookies = document.cookie.split(';');
            for (let cookie of cookies) {
                const [name, value] = cookie.trim().split('=');
                if (name.toLowerCase().includes('msal') || 
                    name.toLowerCase().includes('microsoft') || 
                    name.toLowerCase().includes('office365') ||
                    name.toLowerCase().includes('outlook')) {
                    try {
                        const decoded = decodeURIComponent(value);
                        const emailMatch = decoded.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/);
                        if (emailMatch) {
                            return emailMatch[0];
                        }
                    } catch (e) {
                        // Continue if decoding fails
                    }
                }
            }
        } catch (e) {
            // Continue if cookie access fails
        }

        // 4. Check sessionStorage for Microsoft auth data
        try {
            const keys = Object.keys(sessionStorage);
            for (let key of keys) {
                if (key.includes('msal') || key.includes('microsoft') || key.includes('office')) {
                    const value = sessionStorage.getItem(key);
                    if (value) {
                        try {
                            const parsed = JSON.parse(value);
                            if (parsed.username) return parsed.username;
                            if (parsed.account?.username) return parsed.account.username;
                        } catch (parseError) {
                            // Continue if parsing fails
                        }
                    }
                }
            }
        } catch (e) {
            // Continue if sessionStorage access fails
        }

        // 5. Check if user is logged into Microsoft via iframe detection (advanced)
        try {
            // This checks for Microsoft's authentication iframe
            const msalFrame = document.querySelector('iframe[src*="login.microsoftonline.com"]');
            if (msalFrame) {
                // Microsoft auth is active, but we can't get email due to CORS
                // Return Adobe email as best guess
                if (window.adobeIMS?.isSignedInUser()) {
                    const profile = window.adobeIMS.getProfile();
                    if (profile && profile.email) {
                        return profile.email;
                    }
                }
            }
        } catch (e) {
            // Continue if iframe check fails
        }

        // 6. Fallback to Adobe IMS email
        if (window.adobeIMS?.isSignedInUser()) {
            const profile = window.adobeIMS.getProfile();
            if (profile && profile.email) {
                return profile.email;
            }
        }

        // 7. Final fallback to current email state
        return email || '';
    }

    function validateMicrosoftId(email: string): boolean {
        // Trim whitespace
        const trimmedEmail = email.trim();
        
        // Basic email format validation: name@company.com
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        
        return emailRegex.test(trimmedEmail);
    }

    function setDefaultMicrosoftId() {
        const microsoftId = getMicrosoftEmailId();
        setTeamsMicrosoftIdInput(microsoftId);
    }

    // Auto-focus on regions page
    useEffect(() => {
        if (activeWizard === PNSWIZARD.Region) {
            // Focus on the appropriate radio button based on workflow
            setTimeout(() => {
                const focusElement = !showCustomRegions ? 
                    document.getElementById('global-event')?.closest('label') : 
                    document.getElementById('custom-event')?.closest('label');
                if (focusElement) {
                    focusElement.focus();
                }
            }, 100);
        }
    }, [activeWizard, showCustomRegions]);

    // Auto-focus on notifications page
    useEffect(() => {
        if (activeWizard === PNSWIZARD.Notification) {
            // Focus on the Slack checkbox
            setTimeout(() => {
                const slackCheckbox = document.getElementById('slack-checkbox')?.closest('label');
                if (slackCheckbox) {
                    slackCheckbox.focus();
                }
            }, 100);
        }
    }, [activeWizard]);

    return (
        <div className="proactive-notifications-con">
            <div className="status-stripe pns-stripe">
                <div className="container" role="heading" aria-level={2}>{locales.subscription_title}</div>
            </div>
            {showSlackConfirm ? renderSlackConfirm() : <></>}
            {isTeamsEnabled && showTeamsConfirm ? renderTeamsConfirm() : <></>}
            <div className="pns-wizard">
                <div className="container">
                    <div aria-live="assertive" aria-atomic="true" className="sr-only">
                        {loading ? loadingMessage : ''}
                    </div>
                    <div className="steps-progress">
                        <ul className="progressbar" style={{ '--inactive-step-color': '#737373' } as React.CSSProperties}>
                            <li className={`step product ${isWizardCompleted(PNSWIZARD.Product) || activeWizard === PNSWIZARD.Product ? 'active' : ''}`} role="heading" aria-level={3} aria-current={activeWizard === PNSWIZARD.Product ? 'step' : undefined} aria-label={`${locales.select_products}, step ${isWizardCompleted(PNSWIZARD.Product) ? 'completed' : activeWizard === PNSWIZARD.Product ? 'current' : 'pending'}`}>{locales.select_products}</li>
                            <li className={`step region ${isWizardCompleted(PNSWIZARD.Region) || activeWizard === PNSWIZARD.Region ? 'active' : ''}`} role="heading" aria-level={3} aria-current={activeWizard === PNSWIZARD.Region ? 'step' : undefined} aria-label={`${locales.regions_events}, step ${isWizardCompleted(PNSWIZARD.Region) ? 'completed' : activeWizard === PNSWIZARD.Region ? 'current' : 'pending'}`}>{locales.regions_events}</li>
                            <li className={`step notification ${isWizardCompleted(PNSWIZARD.Notification) || activeWizard === PNSWIZARD.Notification ? 'active' : ''}`} role="heading" aria-level={3} aria-current={activeWizard === PNSWIZARD.Notification ? 'step' : undefined} aria-label={`${locales.notifications}, step ${isWizardCompleted(PNSWIZARD.Notification) ? 'completed' : activeWizard === PNSWIZARD.Notification ? 'current' : 'pending'}`}>{locales.notifications}</li>
                            <li className={`step confirm ${isWizardCompleted(PNSWIZARD.Confirm) || activeWizard === PNSWIZARD.Confirm ? 'active' : ''}`} role="heading" aria-level={3} aria-current={activeWizard === PNSWIZARD.Confirm ? 'step' : undefined} aria-label={`${locales.confirm}, step ${isWizardCompleted(PNSWIZARD.Confirm) ? 'completed' : activeWizard === PNSWIZARD.Confirm ? 'current' : 'pending'}`}>{locales.confirm}</li>
                        </ul>
                    </div>
                    {error ? <GenericError /> : renderPNSWizard()}
                </div>
            </div>
        </div>
    );

}